// src/components/admin/Admin.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../lib/firebase';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { supabase } from '../../lib/supabaseClient';
import { notificationService } from '../../services/notificationService';
import {
  Users, Store, ShoppingBag, Package, Settings, LogOut, HelpCircle, Bug, Lightbulb, MessageSquare,
  Search, X, AlertCircle, CheckCircle, DollarSign,
  Bell, Eye, Edit, Plus, Minus, Star,
  TrendingUp, CreditCard, Trash2, UserX, UserCheck,
  ChevronUp, ChevronDown, Home, Menu, Send, ArrowLeftRight, Clock,
  Shield, UserMinus, MessageCircle as MessageCircleIcon, Reply, Filter, SortAsc, SortDesc,
  RefreshCw, Mail, Smartphone, Inbox, Check, Flag, Loader, Headphones,
  Video, Calendar, Ban, Wallet, ArrowDownToLine, ArrowUpFromLine, History, Printer, Copy, BookOpen,
  Megaphone, Timer, Rocket, Crown, Gem, Sparkles, BadgeCheck, XCircle,
  ShieldCheck, Download, GraduationCap, Play, FileText, Save, Filter as FilterIcon
} from 'lucide-react';
import logo from '../../assets/images/logo.png';
import './Admin.css';
import { adminCacheGetStale, adminCacheSet } from '../../lib/adminCache';
import {
  groupSimilarTerms,
  timeFilterLabels,
  getCutoffMs,
  TimeFilterKey,
  SearchGroup,
} from '../../utils/searchSimilarity';

// -------------------- Types --------------------
interface User {
  id: string;
  firebase_uid: string;
  name: string;
  email: string;
  phone: string;
  avatar_url: string;
  user_type: 'user' | 'vendor' | 'admin' | 'suspended';
  balance: number;
  total_spent: number;
  total_orders: number;
  created_at: string;
  last_seen: string;
  is_active: boolean;
  shops?: { id: string; shop_name: string }[];
}

interface VendorProfile {
  id: string;
  vendor_id: string;
  user_id: string;
  shop_name: string;
  business_email: string;
  contact_phone: string;
  profile_image: string;
  is_active: boolean;
  total_products: number;
  total_sales: number;
  average_rating: number;
  pending_balance: number;
  available_balance: number;
  created_at: string;
}

interface Order {
  id: string;
  order_number: string;
  user_name: string;
  user_email: string;
  total_amount: number;
  status: string;
  payment_status: string;
  created_at: string;
}

interface Product {
  id: string;
  vendor_name: string;
  title: string;
  price: number;
  images: string[];
  inventory: number;
  is_active: boolean;
  is_promoted: boolean;
  category: string;
  views_count: number;
  created_at: string;
}

interface Notification {
  id: string;
  title: string;
  body: string;
  notification_type: string;
  created_at: string;
  read: boolean;
}

interface Feedback {
  id: string;
  question: string;
  answer: string;
  sender_id: string;
  sender_name: string;
  sender_email: string;
  feedback_type: string;
  approved: boolean;
  created_at: string;
  updated_at: string;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface SupportRecipient {
  id: string;
  email: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

interface OpenMarketSettings {
  id: number;
  active_status: boolean;
  open_date: string | null;
  close_date: string | null;
  schedule_type: 'once' | 'regular';
  created_at: string;
  updated_at: string;
}

interface OpenMarketVideoRoom {
  id: string;
  room_name: string;
  created_by_user_id: string;
  created_by_name: string;
  participant_count: number;
  is_active: boolean;
  last_activity: string;
  created_at: string;
}

interface OpenMarketPost {
  id: string;
  user_id: string;
  text: string;
  media_urls: string[] | null;
  media_types: string[] | null;
  created_at: string;
  expires_at: string;
  user_name?: string;
  user_avatar?: string;
  likes_count?: number;
  comments_count?: number;
}

interface OpenMarketBlacklistEntry {
  id: string;
  user_id: string;
  reason: string | null;
  created_by: string;
  created_at: string;
}

interface OverviewStats {
  totalUsers: number;
  totalVendors: number;
  totalAdmins: number;
  totalProducts: number;
  totalOrders: number;
  totalRevenue: number;
  totalUserBalance: number;
  totalVendorPending: number;
  totalVendorAvailable: number;
  ordersLast7Days: number;
  usersLast7Days: number;
}

// Money Record Types
interface MoneyRecord {
  id: string;
  user_id: string;
  recipient_id: string | null;
  amount: number;
  type: 'deposit' | 'withdrawal' | 'transfer';
  status: string;
  approval: boolean;
  receipt_base64: string | null;
  bank_name: string | null;
  account_number: string | null;
  account_name: string | null;
  description: string | null;
  admin_notes: string | null;
  sender_previous_balance: number | null;
  sender_new_balance: number | null;
  recipient_previous_balance: number | null;
  recipient_new_balance: number | null;
  created_at: string;
  updated_at: string;
}

interface EnrichedMoneyRecord extends MoneyRecord {
  sender_name?: string;
  sender_email?: string;
  sender_avatar?: string;
  recipient_name?: string;
  recipient_email?: string;
  recipient_avatar?: string;
}

interface Bank {
  name: string;
  code: string;
  slug: string;
}

// ==== Ads Types ====
interface BusinessAd {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  target_type: string;
  target_id: string;
  target_name: string | null;
  target_image: string | null;
  target_category: string | null;
  plan_id: string;
  plan_name: string;
  amount: number;
  duration_days: number;
  status: 'pending' | 'running' | 'expired' | 'failed' | string;
  payment_reference: string | null;
  failure_reason: string | null;
  started_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string | null;
  extra: any;
}

// ==== Verified Shops Types ====
type VerifiedPlan = 'silver' | 'gold' | 'diamond';
type VerifiedStatus = 'in_progress' | 'pending_review' | 'success' | 'rejected' | 'expired';

interface VerifiedShop {
  id: string;
  user_id: string;
  shop_id: string;
  plan: VerifiedPlan;
  status: VerifiedStatus;
  nin_image_url: string | null;
  proof_of_address_url: string | null;
  cac_image_url: string | null;
  admin_note: string | null;
  fee_paid: number;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

interface EnrichedVerifiedShop extends VerifiedShop {
  user_name?: string;
  user_email?: string;
  user_avatar?: string;
  shop_name?: string;
  shop_profile_image?: string;
}

// ==== Learning Types ====
interface LearningVideo {
  id: string;
  title: string;
  description: string | null;
  video_url: string | null;
  video_url_1: string | null;
  video_url_2: string | null;
  video_url_3: string | null;
  video_url_4: string | null;
  video_url_5: string | null;
  thumbnail_url: string | null;
  views_count: number;
  total_stars: number;
  total_reviews: number;
  average_rating: number;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ==== Search Insights (main marketplace search_history table) ====
interface SearchHistoryRow {
  id: string;
  user_id: string | null;
  search_term: string;
  frequency: number | null;
  created_at: string;
  updated_at: string | null;
}

// ==== Learning Search Insights ====
interface LearningSearchRow {
  id: string;
  user_id: string | null;
  search_term: string;
  created_at: string;
}

type SortDirection = 'asc' | 'desc';
interface SortConfig {
  key: string;
  direction: SortDirection;
}

type FeedbackFilterType = 'all' | 'complaint' | 'bug' | 'suggestion' | 'feature' | 'other';
type FeedbackReplyStatus = 'all' | 'replied' | 'unreplied';
type TabType =
  | 'overview' | 'users' | 'vendors' | 'orders' | 'products'
  | 'notifications' | 'admins' | 'settings' | 'feedbacks' | 'faq'
  | 'support' | 'openmarket' | 'funding' | 'ads' | 'verified'
  | 'learning' | 'learning_search_insight' | 'search_insights';

// ============================================================
// SESSION PERSISTENCE (remember tab + open modal on reload)
// ============================================================
const ADMIN_SESSION_KEY = 'gostorez_admin_session_v1';

interface AdminSessionSnapshot {
  activeTab: TabType;
  // Which modal was open (only one at a time in practice)
  openModal:
    | 'learning_form'
    | 'learning_detail'
    | 'learning_delete'
    | 'search_insights_detail'
    | 'ad_detail'
    | 'verification_detail'
    | 'transaction_detail'
    | 'withdraw_confirm'
    | 'user_balance'
    | 'vendor_fund'
    | 'notification_composer'
    | 'reply_feedback'
    | 'faq_form'
    | 'support_form'
    | 'user_detail'
    | 'vendor_detail'
    | null;
  // Ids to restore the modal's target
  modalIdA?: string | null;
  modalIdB?: string | null;
  // Preserve scroll on admin main
  savedAt: number;
}

const loadAdminSession = (): AdminSessionSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(ADMIN_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminSessionSnapshot;
    // 30 minute expiry for the session snapshot
    if (Date.now() - (parsed.savedAt || 0) > 30 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveAdminSession = (snap: Omit<AdminSessionSnapshot, 'savedAt'>) => {
  try {
    sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({ ...snap, savedAt: Date.now() }));
  } catch {
    /* silent */
  }
};

const clearAdminSession = () => {
  try {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
  } catch {
    /* silent */
  }
};

// Simple infinite scroll
const SimpleInfiniteScroll: React.FC<{
  dataLength: number;
  next: () => void;
  hasMore: boolean;
  loader: React.ReactNode;
  children: React.ReactNode;
}> = ({ dataLength, next, hasMore, loader, children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 100) next();
    };
    const current = scrollRef.current;
    if (current) current.addEventListener('scroll', handleScroll);
    return () => current?.removeEventListener('scroll', handleScroll);
  }, [hasMore, next]);

  return (
    <div ref={scrollRef} className="admin-scroll-container">
      {children}
      {hasMore && dataLength > 0 && loader}
    </div>
  );
};

// ============================================================
// LEARNING FORM STATE
// ============================================================
interface LearningFormState {
  id: string | null;
  title: string;
  description: string;
  video_url: string;
  video_url_1: string;
  video_url_2: string;
  video_url_3: string;
  video_url_4: string;
  video_url_5: string;
  thumbnail_url: string;
  is_active: boolean;
}

const emptyLearningForm: LearningFormState = {
  id: null,
  title: '',
  description: '',
  video_url: '',
  video_url_1: '',
  video_url_2: '',
  video_url_3: '',
  video_url_4: '',
  video_url_5: '',
  thumbnail_url: '',
  is_active: true,
};

// -------------------- Component --------------------
const Admin: React.FC = () => {
  const navigate = useNavigate();

  // ============================================================
  // Restore session on mount (runs synchronously before first render effects)
  // ============================================================
  const restoredSessionRef = useRef<AdminSessionSnapshot | null>(
    typeof window !== 'undefined' ? loadAdminSession() : null
  );

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>(
    restoredSessionRef.current?.activeTab || 'overview'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentAdminName, setCurrentAdminName] = useState('');
  const [currentAdminEmail, setCurrentAdminEmail] = useState('');
  const [currentAdminUid, setCurrentAdminUid] = useState('');

  // Data states
  const [users, setUsers] = useState<User[]>([]);
  const [admins, setAdmins] = useState<User[]>([]);
  const [vendors, setVendors] = useState<VendorProfile[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [supportRecipients, setSupportRecipients] = useState<SupportRecipient[]>([]);

  // ==== Ads state ====
  const [ads, setAds] = useState<BusinessAd[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsStatusFilter, setAdsStatusFilter] = useState<'all' | 'running' | 'expired' | 'failed' | 'pending'>('all');
  const [adsPlanFilter, setAdsPlanFilter] = useState<'all' | 'startup' | 'advanced' | 'premium' | 'custom'>('all');
  const [adsSearch, setAdsSearch] = useState('');
  const [adsStats, setAdsStats] = useState({
    total: 0,
    running: 0,
    expired: 0,
    failed: 0,
    totalRevenue: 0,
    byPlan: {} as Record<string, number>
  });
  const [selectedAd, setSelectedAd] = useState<BusinessAd | null>(null);
  const [showAdDetailModal, setShowAdDetailModal] = useState(false);

  // ==== Verified state ====
  const [verifiedShops, setVerifiedShops] = useState<EnrichedVerifiedShop[]>([]);
  const [verifiedLoading, setVerifiedLoading] = useState(false);
  const [verifiedStatusFilter, setVerifiedStatusFilter] = useState<'all' | VerifiedStatus>('pending_review');
  const [verifiedPlanFilter, setVerifiedPlanFilter] = useState<'all' | VerifiedPlan>('all');
  const [verifiedSearch, setVerifiedSearch] = useState('');
  const [selectedVerification, setSelectedVerification] = useState<EnrichedVerifiedShop | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationAdminNote, setVerificationAdminNote] = useState('');
  const [verificationNewStatus, setVerificationNewStatus] = useState<VerifiedStatus>('success');
  const [savingVerification, setSavingVerification] = useState(false);
  const [verificationStats, setVerificationStats] = useState({
    total: 0,
    in_progress: 0,
    pending_review: 0,
    success: 0,
    rejected: 0,
    expired: 0,
    revenue: 0
  });

  // ==== Learning state ====
  const [learningVideos, setLearningVideos] = useState<LearningVideo[]>([]);
  const [learningLoading, setLearningLoading] = useState(false);
  const [showLearningFormModal, setShowLearningFormModal] = useState(false);
  const [learningForm, setLearningForm] = useState<LearningFormState>(emptyLearningForm);
  const [savingLearning, setSavingLearning] = useState(false);
  const [selectedLearningVideo, setSelectedLearningVideo] = useState<LearningVideo | null>(null);
  const [showLearningDetailModal, setShowLearningDetailModal] = useState(false);
  const [learningEditMode, setLearningEditMode] = useState(false);
  const [learningNoteDraft, setLearningNoteDraft] = useState('');
  const [learningDeleteTarget, setLearningDeleteTarget] = useState<LearningVideo | null>(null);
  const [showLearningDeleteModal, setShowLearningDeleteModal] = useState(false);
  // Pending restore — if we need to reopen a learning modal after data loads
  const [pendingLearningRestore, setPendingLearningRestore] = useState<{
    modal: 'learning_form' | 'learning_detail' | 'learning_delete';
    videoId: string;
  } | null>(
    restoredSessionRef.current?.openModal === 'learning_form' ||
      restoredSessionRef.current?.openModal === 'learning_detail' ||
      restoredSessionRef.current?.openModal === 'learning_delete'
      ? {
          modal: restoredSessionRef.current.openModal as any,
          videoId: (restoredSessionRef.current.modalIdA as string) || '',
        }
      : null
  );

  // ==== Main Search Insights ====
  const [searchInsightsRows, setSearchInsightsRows] = useState<SearchHistoryRow[]>([]);
  const [searchInsightsLoading, setSearchInsightsLoading] = useState(false);
  const [searchInsightsTimeFilter, setSearchInsightsTimeFilter] = useState<TimeFilterKey>('last7d');
  const [searchInsightsSimilarity, setSearchInsightsSimilarity] = useState<number>(90);
  const [searchInsightsGroupingOn, setSearchInsightsGroupingOn] = useState<boolean>(true);
  const [showSearchInsightsFilter, setShowSearchInsightsFilter] = useState(false);
  const [showSearchInsightsDetailModal, setShowSearchInsightsDetailModal] = useState(false);
  const [searchInsightsDetail, setSearchInsightsDetail] = useState<SearchGroup | null>(null);
  const [searchInsightsDetailKey, setSearchInsightsDetailKey] = useState<string | null>(
    restoredSessionRef.current?.openModal === 'search_insights_detail'
      ? (restoredSessionRef.current.modalIdA as string) || null
      : null
  );

  // ==== Learning Search Insights ====
  const [learningInsightsRows, setLearningInsightsRows] = useState<LearningSearchRow[]>([]);
  const [learningInsightsLoading, setLearningInsightsLoading] = useState(false);

  // Open Market Data states
  const [openMarketSettings, setOpenMarketSettings] = useState<OpenMarketSettings | null>(null);
  const [openMarketVideoRooms, setOpenMarketVideoRooms] = useState<OpenMarketVideoRoom[]>([]);
  const [openMarketPosts, setOpenMarketPosts] = useState<OpenMarketPost[]>([]);
  const [openMarketBlacklist, setOpenMarketBlacklist] = useState<OpenMarketBlacklistEntry[]>([]);

  // Open Market form states
  const [omActiveStatus, setOmActiveStatus] = useState(false);
  const [omOpenDate, setOmOpenDate] = useState('');
  const [omCloseDate, setOmCloseDate] = useState('');
  const [omScheduleType, setOmScheduleType] = useState<'once' | 'regular'>('once');
  const [savingOpenMarket, setSavingOpenMarket] = useState(false);

  const [stats, setStats] = useState<OverviewStats>({
    totalUsers: 0, totalVendors: 0, totalAdmins: 0, totalProducts: 0, totalOrders: 0,
    totalRevenue: 0, totalUserBalance: 0, totalVendorPending: 0,
    totalVendorAvailable: 0, ordersLast7Days: 0, usersLast7Days: 0
  });

  // Funding Tab States
  const [moneyRecords, setMoneyRecords] = useState<MoneyRecord[]>([]);
  const [enrichedMoneyRecords, setEnrichedMoneyRecords] = useState<EnrichedMoneyRecord[]>([]);
  const [fundingFilter, setFundingFilter] = useState<'all' | 'deposit' | 'withdrawal' | 'transfer'>('all');
  const [fundingSort, setFundingSort] = useState<'desc' | 'asc'>('desc');
  const [selectedTransaction, setSelectedTransaction] = useState<EnrichedMoneyRecord | null>(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [fundingStats, setFundingStats] = useState({
    totalBalance: 0,
    totalPendingDeposit: 0,
    totalPendingWithdrawal: 0,
    totalCompletedDeposit: 0,
    totalCompletedWithdrawal: 0,
    totalTransfers: 0
  });
  const [banks, setBanks] = useState<Bank[]>([]);
  const [withdrawApprovalData, setWithdrawApprovalData] = useState<{
    recordId: string;
    amount: number;
    bankName: string;
    accountNumber: string;
    accountName: string;
    userId: string;
    userEmail: string;
    userName: string;
  } | null>(null);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);

  // Search & filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'created_at', direction: 'desc' });

  // Feedback specific filters
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<FeedbackFilterType>('all');
  const [feedbackReplyFilter, setFeedbackReplyFilter] = useState<FeedbackReplyStatus>('all');
  const [feedbackSort, setFeedbackSort] = useState<'recent' | 'oldest'>('recent');

  // Pagination
  const [usersPage, setUsersPage] = useState(0);
  const [vendorsPage, setVendorsPage] = useState(0);
  const [ordersPage, setOrdersPage] = useState(0);
  const [productsPage, setProductsPage] = useState(0);
  const [adminsPage, setAdminsPage] = useState(0);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [hasMoreVendors, setHasMoreVendors] = useState(true);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [hasMoreAdmins, setHasMoreAdmins] = useState(true);
  const PAGE_SIZE = 50;

  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [balanceAmount, setBalanceAmount] = useState<number | ''>('');
  const [balanceAction, setBalanceAction] = useState<'credit' | 'debit'>('credit');
  const [balanceReason, setBalanceReason] = useState('');
  const [processingBalance, setProcessingBalance] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'user' | 'vendor' | 'product' | 'faq' | 'support' | 'openmarket_post' | 'openmarket_room' | 'openmarket_blacklist'; id: string; name: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [showVendorFundModal, setShowVendorFundModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<VendorProfile | null>(null);
  const [fundAmount, setFundAmount] = useState<number | ''>('');
  const [fundDirection, setFundDirection] = useState<'toAvailable' | 'toPending'>('toAvailable');
  const [processingFund, setProcessingFund] = useState(false);

  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationTarget, setNotificationTarget] = useState<'all' | 'users' | 'vendors' | 'admins' | 'custom'>('all');
  const [customUserIds, setCustomUserIds] = useState<string[]>([]);
  const [customUserIdInput, setCustomUserIdInput] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);

  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [userDetail, setUserDetail] = useState<User | null>(null);
  const [showVendorDetailModal, setShowVendorDetailModal] = useState(false);
  const [vendorDetail, setVendorDetail] = useState<VendorProfile | null>(null);

  // Feedback specific modals
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [togglingApprove, setTogglingApprove] = useState<string | null>(null);

  // FAQ specific modals
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [faqQuestion, setFaqQuestion] = useState('');
  const [faqAnswer, setFaqAnswer] = useState('');
  const [faqOrderIndex, setFaqOrderIndex] = useState(0);
  const [savingFaq, setSavingFaq] = useState(false);

  // Support specific modals
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [editingSupport, setEditingSupport] = useState<SupportRecipient | null>(null);
  const [supportEmail, setSupportEmail] = useState('');
  const [supportUserId, setSupportUserId] = useState('');
  const [savingSupport, setSavingSupport] = useState(false);

  // Refs
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // ============================================================
  // SESSION PERSISTENCE — auto-save whenever admin state changes
  // ============================================================
  useEffect(() => {
    if (!isAdmin) return;

    let openModal: AdminSessionSnapshot['openModal'] = null;
    let modalIdA: string | null = null;
    let modalIdB: string | null = null;

    if (showLearningFormModal) {
      openModal = 'learning_form';
      modalIdA = learningForm.id || '';
    } else if (showLearningDeleteModal && learningDeleteTarget) {
      openModal = 'learning_delete';
      modalIdA = learningDeleteTarget.id;
    } else if (showLearningDetailModal && selectedLearningVideo) {
      openModal = 'learning_detail';
      modalIdA = selectedLearningVideo.id;
    } else if (showSearchInsightsDetailModal && searchInsightsDetail) {
      openModal = 'search_insights_detail';
      modalIdA = searchInsightsDetail.leading;
    } else if (showAdDetailModal && selectedAd) {
      openModal = 'ad_detail';
      modalIdA = selectedAd.id;
    } else if (showVerificationModal && selectedVerification) {
      openModal = 'verification_detail';
      modalIdA = selectedVerification.id;
    } else if (showTransactionModal && selectedTransaction) {
      openModal = 'transaction_detail';
      modalIdA = selectedTransaction.id;
    } else if (showWithdrawConfirm && withdrawApprovalData) {
      openModal = 'withdraw_confirm';
      modalIdA = withdrawApprovalData.recordId;
    } else if (showUserModal && (selectedUser || selectedVendor)) {
      openModal = 'user_balance';
      modalIdA = selectedUser?.id || selectedVendor?.id || null;
    } else if (showVendorFundModal && selectedVendor) {
      openModal = 'vendor_fund';
      modalIdA = selectedVendor.id;
    } else if (showNotificationModal) {
      openModal = 'notification_composer';
    } else if (showReplyModal && selectedFeedback) {
      openModal = 'reply_feedback';
      modalIdA = selectedFeedback.id;
    } else if (showFaqModal) {
      openModal = 'faq_form';
      modalIdA = editingFaq?.id || null;
    } else if (showSupportModal) {
      openModal = 'support_form';
      modalIdA = editingSupport?.id || null;
    } else if (showUserDetailModal && userDetail) {
      openModal = 'user_detail';
      modalIdA = userDetail.id;
    } else if (showVendorDetailModal && vendorDetail) {
      openModal = 'vendor_detail';
      modalIdA = vendorDetail.id;
    }

    saveAdminSession({
      activeTab,
      openModal,
      modalIdA,
      modalIdB,
    });
  }, [
    isAdmin,
    activeTab,
    showLearningFormModal, learningForm.id,
    showLearningDeleteModal, learningDeleteTarget,
    showLearningDetailModal, selectedLearningVideo,
    showSearchInsightsDetailModal, searchInsightsDetail,
    showAdDetailModal, selectedAd,
    showVerificationModal, selectedVerification,
    showTransactionModal, selectedTransaction,
    showWithdrawConfirm, withdrawApprovalData,
    showUserModal, selectedUser, selectedVendor,
    showVendorFundModal,
    showNotificationModal,
    showReplyModal, selectedFeedback,
    showFaqModal, editingFaq,
    showSupportModal, editingSupport,
    showUserDetailModal, userDetail,
    showVendorDetailModal, vendorDetail,
  ]);

  // ============================================================
  // Back button interception — closes closest modal first
  // ============================================================
  const closeClosestModal = useCallback((): boolean => {
    if (showLearningDeleteModal) { setShowLearningDeleteModal(false); setLearningDeleteTarget(null); return true; }
    if (showSearchInsightsDetailModal) { setShowSearchInsightsDetailModal(false); setSearchInsightsDetail(null); setSearchInsightsDetailKey(null); return true; }
    if (showLearningDetailModal) { setShowLearningDetailModal(false); setLearningEditMode(false); setSelectedLearningVideo(null); return true; }
    if (showLearningFormModal) { setShowLearningFormModal(false); setLearningForm(emptyLearningForm); return true; }
    if (showVerificationModal) { setShowVerificationModal(false); setSelectedVerification(null); return true; }
    if (showAdDetailModal) { setShowAdDetailModal(false); setSelectedAd(null); return true; }
    if (showTransactionModal) { setShowTransactionModal(false); setSelectedTransaction(null); return true; }
    if (showWithdrawConfirm) { setShowWithdrawConfirm(false); setWithdrawApprovalData(null); return true; }
    if (showUserModal) { setShowUserModal(false); setSelectedUser(null); return true; }
    if (showVendorFundModal) { setShowVendorFundModal(false); setSelectedVendor(null); return true; }
    if (showNotificationModal) { setShowNotificationModal(false); return true; }
    if (showReplyModal) { setShowReplyModal(false); setSelectedFeedback(null); return true; }
    if (showFaqModal) { setShowFaqModal(false); setEditingFaq(null); return true; }
    if (showSupportModal) { setShowSupportModal(false); setEditingSupport(null); return true; }
    if (deleteTarget) { setDeleteTarget(null); return true; }
    if (showUserDetailModal) { setShowUserDetailModal(false); return true; }
    if (showVendorDetailModal) { setShowVendorDetailModal(false); return true; }
    if (sidebarOpen) { setSidebarOpen(false); return true; }
    return false;
  }, [
    showLearningDeleteModal, showSearchInsightsDetailModal, showLearningDetailModal,
    showLearningFormModal, showVerificationModal, showAdDetailModal,
    showTransactionModal, showWithdrawConfirm, showUserModal,
    showVendorFundModal, showNotificationModal, showReplyModal,
    showFaqModal, showSupportModal, deleteTarget, showUserDetailModal,
    showVendorDetailModal, sidebarOpen
  ]);

  useEffect(() => {
    window.history.pushState({ adminModal: true }, '');

    const onPop = () => {
      const closed = closeClosestModal();
      if (closed) {
        window.history.pushState({ adminModal: true }, '');
      } else {
        navigate(-1);
      }
    };

    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [closeClosestModal, navigate]);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sidebarOpen && sidebarRef.current && !sidebarRef.current.contains(e.target as Node)) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch banks for withdrawal
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await fetch('http://localhost:3000/api/pay/banks');
        const data = await response.json();
        if (data.success) {
          setBanks(data.banks);
        }
      } catch (error) {
        console.error('Failed to fetch banks:', error);
      }
    };
    fetchBanks();
  }, []);

  // ============================================================
  // LEARNING HELPERS — WRITE OPS (fixed)
  // ------------------------------------------------------------
  // Every write goes through one of these helpers. They:
  //   1. Confirm the Supabase session has a token (login state).
  //   2. Fire the write.
  //   3. If it 401s, log the exact response body so it's debuggable.
  // ============================================================
  const ensureSupabaseSession = async (): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return !!session?.access_token;
    } catch {
      return false;
    }
  };

  const saveLearningVideo = async (payload: any, id: string | null): Promise<LearningVideo> => {
    const hasSession = await ensureSupabaseSession();
    if (!hasSession) {
      // Fall back to anon key (RLS policies above allow anon writes).
      // We still log it, so you can see if the anon key is missing.
      console.warn('[Admin] No Supabase auth session — attempting anon write.');
    }

    if (id) {
      const { data, error } = await supabase
        .from('learning_videos')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        console.error('[Admin] learning_videos UPDATE failed:', error);
        throw new Error(error.message || 'Failed to update tutorial.');
      }
      return data as LearningVideo;
    }

    const { data, error } = await supabase
      .from('learning_videos')
      .insert(payload)
      .select()
      .single();
    if (error) {
      console.error('[Admin] learning_videos INSERT failed:', error);
      throw new Error(error.message || 'Failed to post tutorial.');
    }
    return data as LearningVideo;
  };

  const deleteLearningVideo = async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('learning_videos')
      .delete()
      .eq('id', id);
    if (error) {
      console.error('[Admin] learning_videos DELETE failed:', error);
      throw new Error(error.message || 'Failed to delete tutorial.');
    }
  };

  const loadLearningVideos = async () => {
    setLearningLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('learning_videos')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) throw err;

      const rows = (data || []) as LearningVideo[];
      setLearningVideos(rows);
      await adminCacheSet('learning_videos', rows);

      // If we owe a modal restore, do it now
      if (pendingLearningRestore && pendingLearningRestore.videoId) {
        const target = rows.find(v => v.id === pendingLearningRestore.videoId);
        if (target) {
          if (pendingLearningRestore.modal === 'learning_form') {
            setLearningForm({
              id: target.id,
              title: target.title || '',
              description: target.description || '',
              video_url: target.video_url || '',
              video_url_1: target.video_url_1 || '',
              video_url_2: target.video_url_2 || '',
              video_url_3: target.video_url_3 || '',
              video_url_4: target.video_url_4 || '',
              video_url_5: target.video_url_5 || '',
              thumbnail_url: target.thumbnail_url || '',
              is_active: target.is_active,
            });
            setShowLearningFormModal(true);
          } else if (pendingLearningRestore.modal === 'learning_detail') {
            setSelectedLearningVideo(target);
            setShowLearningDetailModal(true);
          } else if (pendingLearningRestore.modal === 'learning_delete') {
            setLearningDeleteTarget(target);
            setShowLearningDeleteModal(true);
          }
        }
        setPendingLearningRestore(null);
      }
    } catch (err) {
      console.error('Error loading learning videos:', err);
    } finally {
      setLearningLoading(false);
    }
  };

  const openLearningForm = (video?: LearningVideo) => {
    if (video) {
      setLearningForm({
        id: video.id,
        title: video.title || '',
        description: video.description || '',
        video_url: video.video_url || '',
        video_url_1: video.video_url_1 || '',
        video_url_2: video.video_url_2 || '',
        video_url_3: video.video_url_3 || '',
        video_url_4: video.video_url_4 || '',
        video_url_5: video.video_url_5 || '',
        thumbnail_url: video.thumbnail_url || '',
        is_active: video.is_active,
      });
    } else {
      setLearningForm(emptyLearningForm);
    }
    setShowLearningFormModal(true);
  };

  const handleSaveLearning = async () => {
    setSavingLearning(true);
    try {
      const payload: any = {
        title: learningForm.title || null,
        description: learningForm.description || null,
        video_url: learningForm.video_url || null,
        video_url_1: learningForm.video_url_1 || null,
        video_url_2: learningForm.video_url_2 || null,
        video_url_3: learningForm.video_url_3 || null,
        video_url_4: learningForm.video_url_4 || null,
        video_url_5: learningForm.video_url_5 || null,
        thumbnail_url: learningForm.thumbnail_url || null,
        is_active: learningForm.is_active,
        updated_at: new Date().toISOString(),
      };

      if (!learningForm.id) {
        // Defaults required for a brand-new row
        payload.created_at = new Date().toISOString();
        payload.views_count = 0;
        payload.total_stars = 0;
        payload.total_reviews = 0;
        payload.average_rating = 0;
        payload.notes = null;
      }

      await saveLearningVideo(payload, learningForm.id);

      setSuccessMessage(learningForm.id ? 'Tutorial updated successfully.' : 'Tutorial posted successfully.');
      setShowLearningFormModal(false);
      setLearningForm(emptyLearningForm);
      await loadLearningVideos();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSavingLearning(false);
    }
  };

  const handleDeleteLearning = async () => {
    if (!learningDeleteTarget) return;
    try {
      await deleteLearningVideo(learningDeleteTarget.id);
      setSuccessMessage('Tutorial deleted.');
      setShowLearningDeleteModal(false);
      setLearningDeleteTarget(null);
      setShowLearningDetailModal(false);
      setSelectedLearningVideo(null);
      await loadLearningVideos();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
  };

  const handleAddLearningNote = async () => {
    if (!selectedLearningVideo || !learningNoteDraft.trim()) return;
    try {
      const existing = (selectedLearningVideo.notes || '')
        .split('#')
        .map(s => s.trim())
        .filter(Boolean);
      existing.push(learningNoteDraft.trim());
      const newNotes = '#' + existing.join('#');

      const { error: err } = await supabase
        .from('learning_videos')
        .update({ notes: newNotes, updated_at: new Date().toISOString() })
        .eq('id', selectedLearningVideo.id);
      if (err) throw err;

      const updated = { ...selectedLearningVideo, notes: newNotes };
      setSelectedLearningVideo(updated);
      setLearningVideos(prev => prev.map(v => v.id === updated.id ? updated : v));
      setLearningNoteDraft('');
      setSuccessMessage('Note added.');
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
  };

  const handleEditLearningNote = async (index: number, newValue: string) => {
    if (!selectedLearningVideo) return;
    const notesArr = (selectedLearningVideo.notes || '')
      .split('#')
      .map(s => s.trim())
      .filter(Boolean);
    notesArr[index] = newValue;
    const newNotes = notesArr.length ? '#' + notesArr.join('#') : null;

    try {
      const { error: err } = await supabase
        .from('learning_videos')
        .update({ notes: newNotes, updated_at: new Date().toISOString() })
        .eq('id', selectedLearningVideo.id);
      if (err) throw err;

      const updated = { ...selectedLearningVideo, notes: newNotes };
      setSelectedLearningVideo(updated);
      setLearningVideos(prev => prev.map(v => v.id === updated.id ? updated : v));
      setSuccessMessage('Note updated.');
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
  };

  const handleDeleteLearningNote = async (index: number) => {
    if (!selectedLearningVideo) return;
    const notesArr = (selectedLearningVideo.notes || '')
      .split('#')
      .map(s => s.trim())
      .filter(Boolean);
    notesArr.splice(index, 1);
    const newNotes = notesArr.length ? '#' + notesArr.join('#') : null;

    try {
      const { error: err } = await supabase
        .from('learning_videos')
        .update({ notes: newNotes, updated_at: new Date().toISOString() })
        .eq('id', selectedLearningVideo.id);
      if (err) throw err;

      const updated = { ...selectedLearningVideo, notes: newNotes };
      setSelectedLearningVideo(updated);
      setLearningVideos(prev => prev.map(v => v.id === updated.id ? updated : v));
      setSuccessMessage('Note deleted.');
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
  };

  // ============================================================
  // MAIN SEARCH INSIGHTS LOADER
  // ============================================================
  const loadSearchInsights = async () => {
    setSearchInsightsLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('search_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(2000);

      if (err) throw err;

      const rows = (data || []) as SearchHistoryRow[];
      setSearchInsightsRows(rows);
      await adminCacheSet('search_insights', rows);
    } catch (err) {
      console.error('Error loading search insights:', err);
    } finally {
      setSearchInsightsLoading(false);
    }
  };

  // ============================================================
  // LEARNING SEARCH INSIGHTS LOADER
  // ============================================================
  const loadLearningInsights = async () => {
    setLearningInsightsLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('learning_search_history')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(2000);

      if (err) throw err;

      const rows = (data || []) as LearningSearchRow[];
      setLearningInsightsRows(rows);
      await adminCacheSet('learning_insights', rows);
    } catch (err) {
      console.error('Error loading learning insights:', err);
    } finally {
      setLearningInsightsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'search_insights') loadSearchInsights();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'learning_search_insight') loadLearningInsights();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'learning' && learningVideos.length === 0) loadLearningVideos();
  }, [activeTab]);

  // ============================================================
  // AUTH CHECK (with cache + lazy loading + session restore)
  // ============================================================
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) { navigate('/signin'); return; }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) { setError('User profile not found'); setLoading(false); return; }
        const userData = userDoc.data();
        if (userData.role !== 'admin') { navigate('/market'); return; }
        setIsAdmin(true);
        setCurrentAdminName(userData.name || 'Admin');
        setCurrentAdminEmail(userData.email || '');
        setCurrentAdminUid(user.uid);

        // Paint cached slices immediately
        try {
          const cachedStats = await adminCacheGetStale<OverviewStats>('stats');
          if (cachedStats) setStats(cachedStats);
          const cachedVideos = await adminCacheGetStale<LearningVideo[]>('learning_videos');
          if (cachedVideos) setLearningVideos(cachedVideos);
          const cachedInsights = await adminCacheGetStale<SearchHistoryRow[]>('search_insights');
          if (cachedInsights) setSearchInsightsRows(cachedInsights);
          const cachedLearningInsights = await adminCacheGetStale<LearningSearchRow[]>('learning_insights');
          if (cachedLearningInsights) setLearningInsightsRows(cachedLearningInsights);
        } catch { /* silent */ }

        setLoading(false);

        const run = (fn: () => Promise<void>) =>
          fn().catch((e) => console.warn('Lazy loader failed:', e));

        // First batch — critical data
        run(loadOverviewStats);
        run(() => loadUsers(true));
        run(loadAdminsFromFirestore);

        // Restore pending modal restore for learning right away (before 150ms delay)
        if (pendingLearningRestore) {
          run(loadLearningVideos);
        }

        // Second batch — defer so browser paints first
        setTimeout(() => {
          run(() => loadVendors(true));
          run(() => loadOrders(true));
          run(() => loadProducts(true));
          run(loadNotifications);
          run(loadFeedbacks);
          run(loadFaqs);
          run(loadSupportRecipients);
          run(loadOpenMarketData);
          run(loadMoneyRecords);
          run(loadAds);
          run(loadVerifiedShops);
          if (!pendingLearningRestore) run(loadLearningVideos);
          run(loadSearchInsights);
          run(loadLearningInsights);
        }, 150);
      } catch (err) {
        setError('Failed to verify admin access');
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // -------------------- Ads Loader --------------------
  const loadAds = async () => {
    setAdsLoading(true);
    try {
      try {
        const { data: staleRows } = await supabase
          .from('business_ads')
          .select('id, status, expires_at')
          .in('status', ['running', 'pending']);
        const now = Date.now();
        const toExpire: string[] = [];
        (staleRows || []).forEach((r: any) => {
          if (r.expires_at && new Date(r.expires_at).getTime() < now) toExpire.push(r.id);
        });
        if (toExpire.length > 0) {
          await supabase
            .from('business_ads')
            .update({ status: 'expired', updated_at: new Date().toISOString() })
            .in('id', toExpire);
        }
      } catch { /* non-critical */ }

      const { data, error: adsError } = await supabase
        .from('business_ads')
        .select('*')
        .order('created_at', { ascending: false });

      if (adsError) {
        console.error('Error loading ads:', adsError);
        setAds([]);
        setAdsStats({ total: 0, running: 0, expired: 0, failed: 0, totalRevenue: 0, byPlan: {} });
        return;
      }

      const rows = (data || []) as BusinessAd[];
      setAds(rows);

      const byPlan: Record<string, number> = {};
      let running = 0, expired = 0, failed = 0, revenue = 0;

      rows.forEach(r => {
        byPlan[r.plan_id] = (byPlan[r.plan_id] || 0) + 1;
        if (r.status === 'running') running++;
        else if (r.status === 'expired') expired++;
        else if (r.status === 'failed') failed++;
        if (r.status !== 'failed') revenue += Number(r.amount) || 0;
      });

      setAdsStats({
        total: rows.length,
        running,
        expired,
        failed,
        totalRevenue: revenue,
        byPlan
      });
    } catch (err) {
      console.error('Exception loading ads:', err);
      setAds([]);
    } finally {
      setAdsLoading(false);
    }
  };

  // -------------------- Verified Shops Loader --------------------
  const loadVerifiedShops = async () => {
    setVerifiedLoading(true);
    try {
      const { data, error: vErr } = await supabase
        .from('verified_shops')
        .select('*')
        .order('updated_at', { ascending: false });

      if (vErr) {
        console.error('Error loading verified shops:', vErr);
        setVerifiedShops([]);
        setVerificationStats({ total: 0, in_progress: 0, pending_review: 0, success: 0, rejected: 0, expired: 0, revenue: 0 });
        return;
      }

      const rows = (data || []) as VerifiedShop[];

      const enriched: EnrichedVerifiedShop[] = await Promise.all(
        rows.map(async (row) => {
          const out: EnrichedVerifiedShop = { ...row };
          try {
            const { data: u } = await supabase
              .from('users')
              .select('name, email, avatar_url')
              .eq('firebase_uid', row.user_id)
              .maybeSingle();
            if (u) {
              out.user_name = u.name || '';
              out.user_email = u.email || '';
              out.user_avatar = u.avatar_url || '';
            }
          } catch { /* ignore */ }
          try {
            const { data: s } = await supabase
              .from('vendor_profiles')
              .select('shop_name, profile_image')
              .eq('vendor_id', row.shop_id)
              .maybeSingle();
            if (s) {
              out.shop_name = s.shop_name || '';
              out.shop_profile_image = s.profile_image || '';
            }
          } catch { /* ignore */ }
          return out;
        })
      );

      setVerifiedShops(enriched);

      const stats = {
        total: enriched.length,
        in_progress: enriched.filter(r => r.status === 'in_progress').length,
        pending_review: enriched.filter(r => r.status === 'pending_review').length,
        success: enriched.filter(r => r.status === 'success').length,
        rejected: enriched.filter(r => r.status === 'rejected').length,
        expired: enriched.filter(r => r.status === 'expired').length,
        revenue: enriched.reduce((sum, r) => sum + (Number(r.fee_paid) || 0), 0)
      };
      setVerificationStats(stats);
    } catch (err) {
      console.error('Exception loading verified shops:', err);
      setVerifiedShops([]);
    } finally {
      setVerifiedLoading(false);
    }
  };

  // -------------------- Save Verification Update --------------------
  const handleSaveVerification = async () => {
    if (!selectedVerification) return;
    setSavingVerification(true);
    try {
      const nowIso = new Date().toISOString();
      const { error: upErr } = await supabase
        .from('verified_shops')
        .update({
          status: verificationNewStatus,
          admin_note: verificationAdminNote || null,
          updated_at: nowIso
        })
        .eq('id', selectedVerification.id);

      if (upErr) throw upErr;

      try {
        await sendNotificationToUser(
          selectedVerification.user_id,
          verificationNewStatus === 'success'
            ? '✅ Verification Approved'
            : verificationNewStatus === 'rejected'
            ? '❌ Verification Rejected'
            : verificationNewStatus === 'expired'
            ? '⏰ Verification Expired'
            : `Verification ${verificationNewStatus.replace('_', ' ')}`,
          verificationAdminNote ||
            (verificationNewStatus === 'success'
              ? 'Your shop has been verified successfully.'
              : verificationNewStatus === 'rejected'
              ? 'Your verification was rejected. Please review and resubmit.'
              : verificationNewStatus === 'expired'
              ? 'Your verification badge has expired. Please renew.'
              : 'Your verification status has been updated.'),
          '/vendor/dashboard'
        );
      } catch { /* non-critical */ }

      setSuccessMessage(`Verification updated to "${verificationNewStatus}".`);
      setShowVerificationModal(false);
      setSelectedVerification(null);
      setVerificationAdminNote('');
      await loadVerifiedShops();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSavingVerification(false);
    }
  };

  // -------------------- Download helpers --------------------
  const downloadImage = (url: string, filename: string) => {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const downloadAllAdMedia = (ad: BusinessAd) => {
    const urls: { url: string; name: string }[] = [];
    if (ad.target_image) {
      urls.push({ url: ad.target_image, name: `ad-${ad.id}-target.jpg` });
    }
    if (urls.length === 0) {
      setSuccessMessage('No media to download.');
      return;
    }
    urls.forEach((item, i) => {
      setTimeout(() => downloadImage(item.url, item.name), i * 200);
    });
    setSuccessMessage(`Downloading ${urls.length} file${urls.length > 1 ? 's' : ''}...`);
  };

  // -------------------- Funding Tab - Load Money Records --------------------
  const loadMoneyRecords = async () => {
    try {
      const { data, error } = await supabase
        .from('money')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMoneyRecords(data || []);

      const enriched = await Promise.all((data || []).map(async (record) => {
        const enrichedRecord: EnrichedMoneyRecord = { ...record };

        if (record.user_id) {
          const { data: sender } = await supabase
            .from('users')
            .select('name, email, avatar_url')
            .eq('id', record.user_id)
            .single();
          if (sender) {
            enrichedRecord.sender_name = sender.name;
            enrichedRecord.sender_email = sender.email;
            enrichedRecord.sender_avatar = sender.avatar_url;
          }
        }

        if (record.recipient_id) {
          const { data: recipient } = await supabase
            .from('users')
            .select('name, email, avatar_url')
            .eq('id', record.recipient_id)
            .single();
          if (recipient) {
            enrichedRecord.recipient_name = recipient.name;
            enrichedRecord.recipient_email = recipient.email;
            enrichedRecord.recipient_avatar = recipient.avatar_url;
          }
        }

        return enrichedRecord;
      }));

      setEnrichedMoneyRecords(enriched);

      const deposits = (data || []).filter(r => r.type === 'deposit');
      const withdrawals = (data || []).filter(r => r.type === 'withdrawal');
      const transfers = (data || []).filter(r => r.type === 'transfer');

      const pendingDeposits = deposits.filter(r => r.status === 'pending');
      const pendingWithdrawals = withdrawals.filter(r => r.status === 'pending');
      const completedDeposits = deposits.filter(r => r.status === 'approved' || r.status === 'completed');
      const completedWithdrawals = withdrawals.filter(r => r.status === 'approved' || r.status === 'completed');

      const totalDeposited = completedDeposits.reduce((sum, r) => sum + r.amount, 0);
      const totalWithdrawn = completedWithdrawals.reduce((sum, r) => sum + r.amount, 0);
      const totalTransferred = transfers.filter(r => r.status === 'completed').reduce((sum, r) => sum + r.amount, 0);

      setFundingStats({
        totalBalance: totalDeposited - totalWithdrawn,
        totalPendingDeposit: pendingDeposits.reduce((sum, r) => sum + r.amount, 0),
        totalPendingWithdrawal: pendingWithdrawals.reduce((sum, r) => sum + r.amount, 0),
        totalCompletedDeposit: totalDeposited,
        totalCompletedWithdrawal: totalWithdrawn,
        totalTransfers: totalTransferred
      });
    } catch (err) {
      console.error('Error loading money records:', err);
    }
  };

  // -------------------- Helper Functions --------------------
  const formatCurrency = (amount: number) => '₦' + amount.toLocaleString('en-NG');

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString('en-NG', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const getStatusColor = (status: string): string => {
    if (status === 'completed' || status === 'approved') return '#10b981';
    if (status === 'pending') return '#f59e0b';
    if (status === 'rejected' || status === 'cancelled') return '#dc2626';
    return '#6c757d';
  };

  const getAmountColor = (record: EnrichedMoneyRecord): string => {
    const { type, status } = record;
    if (type === 'deposit') {
      if (status === 'pending') return '#6c757d';
      if (status === 'approved' || status === 'completed') return '#10b981';
      if (status === 'rejected') return '#adb5bd';
      return '#6c757d';
    }
    if (type === 'withdrawal') {
      if (status === 'pending') return '#f59e0b';
      if (status === 'approved' || status === 'completed') return '#ef4444';
      if (status === 'rejected') return '#adb5bd';
      return '#6c757d';
    }
    if (type === 'transfer') {
      if (status === 'completed') return '#10b981';
      if (status === 'pending') return '#f59e0b';
      return '#adb5bd';
    }
    return '#6c757d';
  };

  const sendNotificationToUser = async (userId: string, title: string, body: string, redirectUrl?: string) => {
    try {
      const notificationData: any = {
        title,
        body,
        notification_type: 'system',
        redirect_url: redirectUrl || '/money',
        data: { from_admin: true, admin_name: currentAdminName }
      };

      notificationData.target_user_id = userId;
      await notificationService.sendNotification(notificationData);

      await supabase.from('notifications').insert({
        title,
        body,
        notification_type: 'system',
        receiver_id: userId,
        receiver_ids: [userId],
        read: false,
        redirect_url: redirectUrl || '/money',
        data: { from_admin: true, admin_name: currentAdminName },
        created_at: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error sending notification:', err);
    }
  };

  // -------------------- Funding Tab - Approve/Decline Handlers --------------------
  const handleApproveDeposit = async (record: EnrichedMoneyRecord) => {
    if (!window.confirm(`Approve deposit of ${formatCurrency(record.amount)} from ${record.sender_name || record.sender_email}?`)) return;

    setProcessingAction(true);
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, balance, firebase_uid')
        .eq('id', record.user_id)
        .single();

      if (userError) throw userError;

      const newBalance = (userData.balance || 0) + record.amount;

      const { error: updateError } = await supabase
        .from('users')
        .update({
          balance: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id);

      if (updateError) throw updateError;

      const { error: moneyError } = await supabase
        .from('money')
        .update({
          status: 'approved',
          approval: true,
          admin_notes: `Approved by ${currentAdminName}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', record.id);

      if (moneyError) throw moneyError;

      await sendNotificationToUser(
        userData.firebase_uid,
        '✅ Deposit Approved',
        `Your deposit of ${formatCurrency(record.amount)} has been approved and credited to your account.`,
        '/money'
      );

      setSuccessMessage(`Deposit of ${formatCurrency(record.amount)} approved successfully!`);
      await loadMoneyRecords();
      await loadOverviewStats();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setProcessingAction(false);
      setShowTransactionModal(false);
      setSelectedTransaction(null);
    }
  };

  const handleDeclineDeposit = async (record: EnrichedMoneyRecord) => {
    if (!window.confirm(`Decline deposit of ${formatCurrency(record.amount)} from ${record.sender_name || record.sender_email}?`)) return;

    setProcessingAction(true);
    try {
      const { error: moneyError } = await supabase
        .from('money')
        .update({
          status: 'rejected',
          approval: false,
          admin_notes: `Declined by ${currentAdminName}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', record.id);

      if (moneyError) throw moneyError;

      await sendNotificationToUser(
        record.sender_email || '',
        '❌ Deposit Declined',
        `Your deposit of ${formatCurrency(record.amount)} was declined. Please contact support for more information.`,
        '/money'
      );

      setSuccessMessage(`Deposit of ${formatCurrency(record.amount)} declined.`);
      await loadMoneyRecords();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setProcessingAction(false);
      setShowTransactionModal(false);
      setSelectedTransaction(null);
    }
  };

  const handleApproveWithdrawal = async (record: EnrichedMoneyRecord) => {
    if (!window.confirm(`Process withdrawal of ${formatCurrency(record.amount)} to ${record.account_name} (${record.bank_name} - ${record.account_number})?`)) return;

    setWithdrawApprovalData({
      recordId: record.id,
      amount: record.amount,
      bankName: record.bank_name || '',
      accountNumber: record.account_number || '',
      accountName: record.account_name || '',
      userId: record.user_id,
      userEmail: record.sender_email || '',
      userName: record.sender_name || ''
    });
    setShowWithdrawConfirm(true);
  };

  const processWithdrawalTransfer = async () => {
    if (!withdrawApprovalData) return;

    setProcessingAction(true);
    setShowWithdrawConfirm(false);

    try {
      const response = await fetch('http://localhost:3000/api/pay/withdraw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: withdrawApprovalData.amount,
          bank_name: withdrawApprovalData.bankName,
          account_number: withdrawApprovalData.accountNumber,
          account_name: withdrawApprovalData.accountName,
          admin_user_id: currentAdminUid,
          reference: `WD-${withdrawApprovalData.recordId}-${Date.now()}`
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Transfer failed');
      }

      const { error: moneyError } = await supabase
        .from('money')
        .update({
          status: 'approved',
          approval: true,
          admin_notes: `Approved and processed by ${currentAdminName}. Transfer ref: ${data.reference}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', withdrawApprovalData.recordId);

      if (moneyError) throw moneyError;

      await sendNotificationToUser(
        withdrawApprovalData.userEmail,
        '✅ Withdrawal Completed',
        `Your withdrawal of ${formatCurrency(withdrawApprovalData.amount)} has been processed and sent to your bank account. Reference: ${data.reference}`,
        '/money'
      );

      setSuccessMessage(`Withdrawal of ${formatCurrency(withdrawApprovalData.amount)} processed successfully!`);
      await loadMoneyRecords();
      await loadOverviewStats();
    } catch (err: any) {
      setSuccessMessage(`Error processing withdrawal: ${err.message}`);
    } finally {
      setProcessingAction(false);
      setShowTransactionModal(false);
      setSelectedTransaction(null);
      setWithdrawApprovalData(null);
    }
  };

  const handleDeclineWithdrawal = async (record: EnrichedMoneyRecord) => {
    if (!window.confirm(`Decline withdrawal of ${formatCurrency(record.amount)} to ${record.account_name}?`)) return;

    setProcessingAction(true);
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, balance, firebase_uid')
        .eq('id', record.user_id)
        .single();

      if (userError) throw userError;

      const newBalance = (userData.balance || 0) + record.amount;

      const { error: updateError } = await supabase
        .from('users')
        .update({
          balance: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', userData.id);

      if (updateError) throw updateError;

      const { error: pendingError } = await supabase
        .from('users')
        .update({
          withdrawal_pending_balance: supabase.rpc('decrement_withdrawal_pending', { amount: record.amount })
        })
        .eq('id', userData.id);

      const { error: moneyError } = await supabase
        .from('money')
        .update({
          status: 'rejected',
          approval: false,
          admin_notes: `Declined by ${currentAdminName}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', record.id);

      if (moneyError) throw moneyError;

      await sendNotificationToUser(
        userData.firebase_uid,
        '❌ Withdrawal Declined',
        `Your withdrawal request of ${formatCurrency(record.amount)} was declined. Funds have been returned to your account.`,
        '/money'
      );

      setSuccessMessage(`Withdrawal of ${formatCurrency(record.amount)} declined and funds refunded.`);
      await loadMoneyRecords();
      await loadOverviewStats();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setProcessingAction(false);
      setShowTransactionModal(false);
      setSelectedTransaction(null);
    }
  };

  // Filtered money records for funding tab
  const filteredMoneyRecords = enrichedMoneyRecords
    .filter(record => {
      if (fundingFilter === 'all') return true;
      return record.type === fundingFilter;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return fundingSort === 'desc' ? dateB - dateA : dateA - dateB;
    });

  // Filtered ads
  const filteredAds = ads.filter(ad => {
    if (adsStatusFilter !== 'all' && ad.status !== adsStatusFilter) return false;
    if (adsPlanFilter !== 'all' && ad.plan_id !== adsPlanFilter) return false;
    if (adsSearch.trim()) {
      const q = adsSearch.toLowerCase();
      const hay = `${ad.user_name || ''} ${ad.user_email || ''} ${ad.target_name || ''} ${ad.plan_name || ''} ${ad.id}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Filtered verified shops
  const filteredVerifiedShops = verifiedShops.filter(v => {
    if (verifiedStatusFilter !== 'all' && v.status !== verifiedStatusFilter) return false;
    if (verifiedPlanFilter !== 'all' && v.plan !== verifiedPlanFilter) return false;
    if (verifiedSearch.trim()) {
      const q = verifiedSearch.toLowerCase();
      const hay = `${v.user_name || ''} ${v.user_email || ''} ${v.shop_name || ''} ${v.shop_id} ${v.user_id}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // ============================================================
  // SEARCH INSIGHTS — derived
  // ============================================================
  const searchInsightsFiltered = useMemo(() => {
    const cutoff = getCutoffMs(searchInsightsTimeFilter);
    return searchInsightsRows.filter(r => new Date(r.created_at).getTime() >= cutoff);
  }, [searchInsightsRows, searchInsightsTimeFilter]);

  const searchInsightsGroups: SearchGroup[] = useMemo(() => {
    const counts = new Map<string, number>();
    searchInsightsFiltered.forEach(r => {
      const t = (r.search_term || '').toLowerCase().trim();
      if (!t) return;
      const w = r.frequency && r.frequency > 0 ? r.frequency : 1;
      counts.set(t, (counts.get(t) || 0) + w);
    });

    const termList = Array.from(counts.entries()).map(([term, count]) => ({ term, count }));

    if (!searchInsightsGroupingOn) {
      return termList
        .sort((a, b) => b.count - a.count)
        .map(t => ({
          leading: t.term,
          totalSearchCount: t.count,
          totalStars: 0,
          members: [{ term: t.term, count: t.count, similarityToLeading: 1 }],
        }));
    }

    return groupSimilarTerms(termList, searchInsightsSimilarity / 100);
  }, [searchInsightsFiltered, searchInsightsGroupingOn, searchInsightsSimilarity]);

  // If we have a pending search-insights detail restore, reopen it once groups are computed
  useEffect(() => {
    if (searchInsightsDetailKey && !showSearchInsightsDetailModal && searchInsightsGroups.length > 0) {
      const match = searchInsightsGroups.find(g => g.leading === searchInsightsDetailKey);
      if (match) {
        setSearchInsightsDetail(match);
        setShowSearchInsightsDetailModal(true);
      }
      setSearchInsightsDetailKey(null);
    }
  }, [searchInsightsDetailKey, searchInsightsGroups, showSearchInsightsDetailModal]);

  // ============================================================
  // LEARNING SEARCH INSIGHTS — newest-first only
  // ============================================================
  const learningInsightsSorted = useMemo(() => {
    return [...learningInsightsRows].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [learningInsightsRows]);

  // -------------------- Open Market Data Loading --------------------
  const loadOpenMarketData = async () => {
    try {
      const { data: settingsData, error: settingsError } = await supabase
        .from('openmarket_settings')
        .select('*')
        .single();
      if (!settingsError && settingsData) {
        setOpenMarketSettings(settingsData);
        setOmActiveStatus(settingsData.active_status);
        setOmOpenDate(settingsData.open_date || '');
        setOmCloseDate(settingsData.close_date || '');
        setOmScheduleType(settingsData.schedule_type || 'once');
      }

      const { data: roomsData, error: roomsError } = await supabase
        .from('openmarket_video_rooms')
        .select('*')
        .order('created_at', { ascending: false });
      if (!roomsError) setOpenMarketVideoRooms(roomsData || []);

      const { data: postsData, error: postsError } = await supabase
        .from('openmarket_posts')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (!postsError) {
        const enrichedPosts = await Promise.all((postsData || []).map(async (post) => {
          const { data: userData } = await supabase
            .from('users')
            .select('name, avatar_url')
            .eq('firebase_uid', post.user_id)
            .single();
          const { count: likesCount } = await supabase
            .from('openmarket_post_likes')
            .select('*', { count: 'exact', head: true })
            .eq('post_id', post.id);
          const { count: commentsCount } = await supabase
            .from('openmarket_comments')
            .select('*', { count: 'exact', head: true })
            .eq('post_id', post.id);
          return {
            ...post,
            user_name: userData?.name || 'User',
            user_avatar: userData?.avatar_url || '',
            likes_count: likesCount || 0,
            comments_count: commentsCount || 0
          };
        }));
        setOpenMarketPosts(enrichedPosts);
      }

      const { data: blacklistData, error: blacklistError } = await supabase
        .from('openmarket_blacklist')
        .select('*')
        .order('created_at', { ascending: false });
      if (!blacklistError) setOpenMarketBlacklist(blacklistData || []);
    } catch (err) {
      console.error('Error loading open market data:', err);
    }
  };

  // -------------------- Open Market Handlers --------------------
  const handleSaveOpenMarketSettings = async () => {
    setSavingOpenMarket(true);
    try {
      const { error } = await supabase
        .from('openmarket_settings')
        .update({
          active_status: omActiveStatus,
          open_date: omOpenDate || null,
          close_date: omCloseDate || null,
          schedule_type: omScheduleType,
          updated_at: new Date().toISOString()
        })
        .eq('id', 1);

      if (error) throw error;
      setSuccessMessage('Open Market settings saved successfully!');
      await loadOpenMarketData();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSavingOpenMarket(false);
    }
  };

  const handleDeleteOpenMarketPost = async (postId: string) => {
    try {
      const { error } = await supabase
        .from('openmarket_posts')
        .delete()
        .eq('id', postId);
      if (error) throw error;
      setSuccessMessage('Post deleted successfully!');
      await loadOpenMarketData();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
    setDeleteTarget(null);
  };

  const handleDeleteOpenMarketRoom = async (roomId: string) => {
    try {
      const { error } = await supabase
        .from('openmarket_video_rooms')
        .delete()
        .eq('id', roomId);
      if (error) throw error;
      setSuccessMessage('Video room deleted successfully!');
      await loadOpenMarketData();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
    setDeleteTarget(null);
  };

  const handleBlockUserFromPost = async (userId: string, userName: string) => {
    if (!window.confirm(`Block user "${userName}" from Open Market? This will delete all their posts, rooms, and comments.`)) return;
    try {
      const { error } = await supabase
        .from('openmarket_blacklist')
        .insert({
          user_id: userId,
          reason: 'Blocked by admin from Open Market',
          created_by: currentAdminUid,
          created_at: new Date().toISOString()
        });
      if (error) throw error;
      setSuccessMessage(`User "${userName}" has been blacklisted. All their content has been removed.`);
      await loadOpenMarketData();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
  };

  const handleUnblockFromBlacklist = async (entryId: string, userId: string) => {
    if (!window.confirm(`Unblock this user? They will be able to post and join live again.`)) return;
    try {
      const { error } = await supabase
        .from('openmarket_blacklist')
        .delete()
        .eq('id', entryId);
      if (error) throw error;
      setSuccessMessage('User unblocked successfully.');
      await loadOpenMarketData();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
  };

  // -------------------- Data loading --------------------
  const loadOverviewStats = async () => {
    try {
      const { count: totalUsers } = await supabase.from('users').select('*', { count: 'exact', head: true });
      const { count: totalVendors } = await supabase.from('vendor_profiles').select('*', { count: 'exact', head: true });

      let totalAdmins = 0;
      try {
        const usersCollection = collection(db, 'users');
        const usersSnapshot = await getDocs(usersCollection);
        totalAdmins = usersSnapshot.docs.filter(doc => doc.data().role === 'admin').length;
      } catch (firestoreErr) { console.error('Error fetching admins from Firestore:', firestoreErr); }

      const { count: totalProducts } = await supabase.from('products').select('*', { count: 'exact', head: true });

      const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoStr = sevenDaysAgo.toISOString();

      const { data: ordersData } = await supabase.from('orders').select('total_amount, payment_status, created_at').eq('payment_status', 'completed');
      const totalRevenue = ordersData?.reduce((sum, o) => sum + o.total_amount, 0) || 0;

      const { count: ordersLast7Days } = await supabase.from('orders').select('*', { count: 'exact', head: true }).gte('created_at', sevenDaysAgoStr);
      const { count: usersLast7Days } = await supabase.from('users').select('*', { count: 'exact', head: true }).gte('created_at', sevenDaysAgoStr);

      const { data: userBalances } = await supabase.from('users').select('balance');
      const totalUserBalance = userBalances?.reduce((sum, u) => sum + (u.balance || 0), 0) || 0;

      const { data: vendorBalances } = await supabase.from('vendor_profiles').select('pending_balance, available_balance');
      const totalVendorPending = vendorBalances?.reduce((sum, v) => sum + (v.pending_balance || 0), 0) || 0;
      const totalVendorAvailable = vendorBalances?.reduce((sum, v) => sum + (v.available_balance || 0), 0) || 0;

      const nextStats: OverviewStats = {
        totalUsers: totalUsers || 0,
        totalVendors: totalVendors || 0,
        totalAdmins: totalAdmins || 0,
        totalProducts: totalProducts || 0,
        totalOrders: ordersData?.length || 0,
        totalRevenue,
        totalUserBalance,
        totalVendorPending,
        totalVendorAvailable,
        ordersLast7Days: ordersLast7Days || 0,
        usersLast7Days: usersLast7Days || 0
      };
      setStats(nextStats);
      await adminCacheSet('stats', nextStats);
    } catch (err) { console.error('Error loading stats:', err); }
  };

  const loadSupportRecipients = async () => {
    try {
      const { data, error } = await supabase
        .from('feedback_support')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSupportRecipients(data || []);
    } catch (err) {
      console.error('Error loading support recipients:', err);
    }
  };

  const loadAdminsFromFirestore = async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const adminUsers: User[] = [];

      for (const docSnapshot of usersSnapshot.docs) {
        const userData = docSnapshot.data();
        if (userData.role === 'admin') {
          const { data: supabaseUser } = await supabase
            .from('users')
            .select('*')
            .eq('firebase_uid', docSnapshot.id)
            .maybeSingle();

          adminUsers.push({
            id: supabaseUser?.id || docSnapshot.id,
            firebase_uid: docSnapshot.id,
            name: userData.name || supabaseUser?.name || '',
            email: userData.email || supabaseUser?.email || '',
            phone: supabaseUser?.phone || '',
            avatar_url: supabaseUser?.avatar_url || '',
            user_type: 'admin',
            balance: supabaseUser?.balance || 0,
            total_spent: supabaseUser?.total_spent || 0,
            total_orders: supabaseUser?.total_orders || 0,
            created_at: supabaseUser?.created_at || new Date().toISOString(),
            last_seen: supabaseUser?.last_seen || '',
            is_active: supabaseUser?.is_active !== false
          });
        }
      }
      setAdmins(adminUsers);
      setHasMoreAdmins(false);
    } catch (err) {
      console.error('Error loading admins from Firestore:', err);
    }
  };

  const loadFeedbacks = async () => {
    try {
      let query = supabase
        .from('feedbacks')
        .select('*');

      if (feedbackTypeFilter !== 'all') {
        query = query.eq('feedback_type', feedbackTypeFilter);
      }

      if (feedbackReplyFilter === 'replied') {
        query = query.not('answer', 'is', null).not('answer', 'eq', '');
      } else if (feedbackReplyFilter === 'unreplied') {
        query = query.or('answer.is.null,answer.eq.');
      }

      const orderDirection = feedbackSort === 'recent' ? 'desc' : 'asc';
      query = query.order('created_at', { ascending: orderDirection === 'asc' });

      const { data, error } = await query;
      if (error) throw error;
      setFeedbacks(data || []);
    } catch (err) {
      console.error('Error loading feedbacks:', err);
    }
  };

  const loadFaqs = async () => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setFaqs(data || []);
    } catch (err) {
      console.error('Error loading FAQs:', err);
    }
  };

  const loadNotifications = async () => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      const filtered = data?.filter(n => n.notification_type !== 'chat') || [];
      setNotifications(filtered);
    } catch (err) { console.error('Error loading notifications:', err); }
  };

  const loadUsers = async (reset = false) => {
    if (reset) { setUsers([]); setUsersPage(0); setHasMoreUsers(true); }
    const page = reset ? 0 : usersPage;
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    try {
      let query = supabase
        .from('users')
        .select('*')
        .order(sortConfig.key, { ascending: sortConfig.direction === 'asc' })
        .range(from, to);

      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%,firebase_uid.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      const usersWithShops = await Promise.all((data || []).map(async (u) => {
        const { data: shops } = await supabase
          .from('vendor_profiles')
          .select('id, shop_name')
          .eq('user_id', u.firebase_uid);
        return { ...u, shops: shops || [] };
      }));

      if (reset) setUsers(usersWithShops);
      else setUsers(prev => [...prev, ...usersWithShops]);
      setHasMoreUsers((data?.length || 0) === PAGE_SIZE);
      if (!reset) setUsersPage(page + 1);
    } catch (err) { console.error('Error loading users:', err); }
  };

  const loadVendors = async (reset = false) => {
    if (reset) { setVendors([]); setVendorsPage(0); setHasMoreVendors(true); }
    const page = reset ? 0 : vendorsPage;
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    try {
      let query = supabase
        .from('vendor_profiles')
        .select('*')
        .order(sortConfig.key, { ascending: sortConfig.direction === 'asc' })
        .range(from, to);

      if (searchTerm) {
        query = query.or(`shop_name.ilike.%${searchTerm}%,business_email.ilike.%${searchTerm}%,vendor_id.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (reset) setVendors(data || []);
      else setVendors(prev => [...prev, ...(data || [])]);
      setHasMoreVendors((data?.length || 0) === PAGE_SIZE);
      if (!reset) setVendorsPage(page + 1);
    } catch (err) { console.error('Error loading vendors:', err); }
  };

  const loadOrders = async (reset = false) => {
    if (reset) { setOrders([]); setOrdersPage(0); setHasMoreOrders(true); }
    const page = reset ? 0 : ordersPage;
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    try {
      let query = supabase
        .from('orders')
        .select('*')
        .order(sortConfig.key, { ascending: sortConfig.direction === 'asc' })
        .range(from, to);

      if (searchTerm) {
        query = query.or(`order_number.ilike.%${searchTerm}%,user_name.ilike.%${searchTerm}%,user_email.ilike.%${searchTerm}%`);
      }
      if (filterStatus !== 'all') query = query.eq('status', filterStatus);

      const { data, error } = await query;
      if (error) throw error;

      if (reset) setOrders(data || []);
      else setOrders(prev => [...prev, ...(data || [])]);
      setHasMoreOrders((data?.length || 0) === PAGE_SIZE);
      if (!reset) setOrdersPage(page + 1);
    } catch (err) { console.error('Error loading orders:', err); }
  };

  const loadProducts = async (reset = false) => {
    if (reset) { setProducts([]); setProductsPage(0); setHasMoreProducts(true); }
    const page = reset ? 0 : productsPage;
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    try {
      let query = supabase
        .from('products')
        .select('*')
        .order(sortConfig.key, { ascending: sortConfig.direction === 'asc' })
        .range(from, to);

      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,vendor_name.ilike.%${searchTerm}%,category.ilike.%${searchTerm}%`);
      }
      if (filterStatus !== 'all') {
        if (filterStatus === 'active') query = query.eq('is_active', true);
        else if (filterStatus === 'inactive') query = query.eq('is_active', false);
        else if (filterStatus === 'promoted') query = query.eq('is_promoted', true);
      }

      const { data, error } = await query;
      if (error) throw error;

      if (reset) setProducts(data || []);
      else setProducts(prev => [...prev, ...(data || [])]);
      setHasMoreProducts((data?.length || 0) === PAGE_SIZE);
      if (!reset) setProductsPage(page + 1);
    } catch (err) { console.error('Error loading products:', err); }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      switch (activeTab) {
        case 'users': loadUsers(true); break;
        case 'vendors': loadVendors(true); break;
        case 'orders': loadOrders(true); break;
        case 'products': loadProducts(true); break;
      }
    }, 500);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchTerm, activeTab]);

  // Sort change
  useEffect(() => {
    switch (activeTab) {
      case 'users': loadUsers(true); break;
      case 'vendors': loadVendors(true); break;
      case 'orders': loadOrders(true); break;
      case 'products': loadProducts(true); break;
    }
  }, [sortConfig]);

  // Filter change
  useEffect(() => {
    if (activeTab === 'orders') loadOrders(true);
    if (activeTab === 'products') loadProducts(true);
    if (activeTab === 'feedbacks') loadFeedbacks();
  }, [filterStatus, feedbackTypeFilter, feedbackReplyFilter, feedbackSort]);

  // -------------------- Feedback Handlers --------------------
  const handleReplyToFeedback = async () => {
    if (!selectedFeedback || !replyText.trim()) return;
    setSendingReply(true);

    try {
      const { error } = await supabase
        .from('feedbacks')
        .update({
          answer: replyText.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedFeedback.id);

      if (error) throw error;

      await sendNotificationToUser(
        selectedFeedback.sender_id,
        `Response to your ${selectedFeedback.feedback_type} feedback`,
        `Admin replied: "${replyText.substring(0, 100)}${replyText.length > 100 ? '...' : ''}"`,
        '/feedbacks'
      );

      setSuccessMessage('Reply sent successfully!');
      setShowReplyModal(false);
      setSelectedFeedback(null);
      setReplyText('');
      await loadFeedbacks();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSendingReply(false);
    }
  };

  const handleToggleApprove = async (feedback: Feedback) => {
    setTogglingApprove(feedback.id);
    try {
      const newApproved = !feedback.approved;
      const { error } = await supabase
        .from('feedbacks')
        .update({
          approved: newApproved,
          updated_at: new Date().toISOString()
        })
        .eq('id', feedback.id);

      if (error) throw error;

      if (newApproved) {
        await sendNotificationToUser(
          feedback.sender_id,
          `Your feedback has been approved`,
          `Thank you for your feedback! It is now visible to the community.`,
          '/feedbacks'
        );
        setSuccessMessage('Feedback approved and user notified!');
      } else {
        setSuccessMessage('Feedback unapproved');
      }

      await loadFeedbacks();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setTogglingApprove(null);
    }
  };

  // -------------------- FAQ Handlers --------------------
  const handleAddFaq = async () => {
    if (!faqQuestion.trim() || !faqAnswer.trim()) {
      setSuccessMessage('Please fill in both question and answer');
      return;
    }
    setSavingFaq(true);

    try {
      const newOrderIndex = faqs.length + 1;
      const { error } = await supabase
        .from('faqs')
        .insert({
          question: faqQuestion.trim(),
          answer: faqAnswer.trim(),
          order_index: newOrderIndex,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setSuccessMessage('FAQ added successfully!');
      setShowFaqModal(false);
      setFaqQuestion('');
      setFaqAnswer('');
      await loadFaqs();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSavingFaq(false);
    }
  };

  const handleEditFaq = (faq: FAQ) => {
    setEditingFaq(faq);
    setFaqQuestion(faq.question);
    setFaqAnswer(faq.answer);
    setFaqOrderIndex(faq.order_index);
    setShowFaqModal(true);
  };

  const handleUpdateFaq = async () => {
    if (!editingFaq) return;
    if (!faqQuestion.trim() || !faqAnswer.trim()) {
      setSuccessMessage('Please fill in both question and answer');
      return;
    }
    setSavingFaq(true);

    try {
      const { error } = await supabase
        .from('faqs')
        .update({
          question: faqQuestion.trim(),
          answer: faqAnswer.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', editingFaq.id);

      if (error) throw error;

      setSuccessMessage('FAQ updated successfully!');
      setShowFaqModal(false);
      setEditingFaq(null);
      setFaqQuestion('');
      setFaqAnswer('');
      await loadFaqs();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSavingFaq(false);
    }
  };

  const handleDeleteFaq = async (faq: FAQ) => {
    try {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', faq.id);

      if (error) throw error;

      setSuccessMessage('FAQ deleted successfully!');
      await loadFaqs();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
    setDeleteTarget(null);
  };

  // -------------------- Support Handlers --------------------
  const handleAddSupport = async () => {
    if (!supportEmail.trim() && !supportUserId.trim()) {
      setSuccessMessage('Please fill in either email or user ID');
      return;
    }
    setSavingSupport(true);

    try {
      const { error } = await supabase
        .from('feedback_support')
        .insert({
          email: supportEmail.trim() || null,
          user_id: supportUserId.trim() || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setSuccessMessage('Support recipient added successfully!');
      setShowSupportModal(false);
      setSupportEmail('');
      setSupportUserId('');
      setEditingSupport(null);
      await loadSupportRecipients();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSavingSupport(false);
    }
  };

  const handleEditSupport = (recipient: SupportRecipient) => {
    setEditingSupport(recipient);
    setSupportEmail(recipient.email || '');
    setSupportUserId(recipient.user_id || '');
    setShowSupportModal(true);
  };

  const handleUpdateSupport = async () => {
    if (!editingSupport) return;
    if (!supportEmail.trim() && !supportUserId.trim()) {
      setSuccessMessage('Please fill in either email or user ID');
      return;
    }
    setSavingSupport(true);

    try {
      const { error } = await supabase
        .from('feedback_support')
        .update({
          email: supportEmail.trim() || null,
          user_id: supportUserId.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingSupport.id);

      if (error) throw error;

      setSuccessMessage('Support recipient updated successfully!');
      setShowSupportModal(false);
      setSupportEmail('');
      setSupportUserId('');
      setEditingSupport(null);
      await loadSupportRecipients();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    } finally {
      setSavingSupport(false);
    }
  };

  const handleDeleteSupport = async (recipient: SupportRecipient) => {
    try {
      const { error } = await supabase
        .from('feedback_support')
        .delete()
        .eq('id', recipient.id);

      if (error) throw error;

      setSuccessMessage('Support recipient deleted successfully!');
      await loadSupportRecipients();
    } catch (err: any) {
      setSuccessMessage(`Error: ${err.message}`);
    }
    setDeleteTarget(null);
  };

  // -------------------- Existing Handlers --------------------
  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffSec < 60) return 'now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString();
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'complaint': return 'Complaint';
      case 'bug': return 'Bug Report';
      case 'suggestion': return 'Suggestion';
      case 'feature': return 'Feature Request';
      default: return 'Other';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'complaint': return <AlertCircle size={14} />;
      case 'bug': return <Bug size={14} />;
      case 'suggestion': return <Lightbulb size={14} />;
      case 'feature': return <Star size={14} />;
      default: return <MessageSquare size={14} />;
    }
  };

  const sendNotificationViaService = async (targetUserIds: string[], title: string, message: string) => {
    try {
      const response = await notificationService.sendNotification({
        title,
        body: message,
        target_user_ids: targetUserIds,
        notification_type: 'system',
        data: { from_admin: true, admin_name: currentAdminName }
      });
      return response;
    } catch (err) {
      console.error('Notification service error:', err);
      throw err;
    }
  };

  const updateUserRole = async (user: User, newRole: 'user' | 'admin' | 'suspended') => {
    try {
      await updateDoc(doc(db, 'users', user.firebase_uid), { role: newRole });
      await supabase
        .from('users')
        .update({ user_type: newRole, updated_at: new Date().toISOString() })
        .eq('id', user.id);
    } catch (err) {
      console.error('Error updating user role:', err);
      throw err;
    }
  };

  const handleMakeAdmin = async (user: User) => {
    if (!window.confirm(`Make ${user.name} an admin?`)) return;
    try {
      await updateUserRole(user, 'admin');
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, user_type: 'admin' } : u));
      await loadAdminsFromFirestore();
      await sendNotificationViaService([user.firebase_uid], 'Admin Privileges Granted', 'You have been made an administrator.');
      setSuccessMessage('User promoted to admin.');
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); }
  };

  const handleRemoveAdmin = async (admin: User) => {
    if (!window.confirm(`Remove ${admin.name} from admin role?`)) return;
    try {
      await updateUserRole(admin, 'user');
      setAdmins(prev => prev.filter(a => a.id !== admin.id));
      setUsers(prev => prev.map(u => u.id === admin.id ? { ...u, user_type: 'user' } : u));
      await sendNotificationViaService([admin.firebase_uid], 'Admin Privileges Revoked', 'You are no longer an administrator.');
      setSuccessMessage('Admin removed.');
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); }
  };

  const handleBalanceAdjust = async () => {
    if (!selectedUser || !balanceAmount) return;
    setProcessingBalance(true);
    try {
      const currentBalance = selectedUser.balance || 0;
      const newBalance = balanceAction === 'credit' ? currentBalance + balanceAmount : currentBalance - balanceAmount;

      const { error: updateError } = await supabase
        .from('users')
        .update({ balance: newBalance, updated_at: new Date().toISOString() })
        .eq('id', selectedUser.id);
      if (updateError) throw updateError;

      await sendNotificationViaService(
        [selectedUser.firebase_uid],
        `Account ${balanceAction === 'credit' ? 'Credited' : 'Debited'}`,
        `Your account has been ${balanceAction === 'credit' ? 'credited with' : 'debited by'} ${formatCurrency(balanceAmount)}. ${balanceReason || ''}`
      );

      setUsers(prev => prev.map(u => u.id === selectedUser.id ? { ...u, balance: newBalance } : u));
      setShowUserModal(false);
      setSelectedUser(null);
      setBalanceAmount('');
      setSuccessMessage(`User ${balanceAction === 'credit' ? 'credited' : 'debited'} successfully!`);
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); } finally { setProcessingBalance(false); }
  };

  const handleVendorFundTransfer = async () => {
    if (!selectedVendor || !fundAmount) return;
    setProcessingFund(true);
    try {
      const currentPending = selectedVendor.pending_balance || 0;
      const currentAvailable = selectedVendor.available_balance || 0;
      let newPending = currentPending, newAvailable = currentAvailable;

      if (fundDirection === 'toAvailable') {
        if (fundAmount > currentPending) throw new Error('Insufficient pending balance');
        newPending = currentPending - fundAmount;
        newAvailable = currentAvailable + fundAmount;
      } else {
        if (fundAmount > currentAvailable) throw new Error('Insufficient available balance');
        newAvailable = currentAvailable - fundAmount;
        newPending = currentPending + fundAmount;
      }

      const { error } = await supabase
        .from('vendor_profiles')
        .update({ pending_balance: newPending, available_balance: newAvailable, updated_at: new Date().toISOString() })
        .eq('id', selectedVendor.id);
      if (error) throw error;

      if (selectedVendor.user_id) {
        await sendNotificationViaService(
          [selectedVendor.user_id],
          'Funds Transferred',
          `Your shop ${selectedVendor.shop_name} had ${formatCurrency(fundAmount)} moved.`
        );
      }

      setVendors(prev => prev.map(v => v.id === selectedVendor.id ? { ...v, pending_balance: newPending, available_balance: newAvailable } : v));
      setShowVendorFundModal(false);
      setSelectedVendor(null);
      setFundAmount('');
      setSuccessMessage('Vendor funds transferred successfully!');
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); } finally { setProcessingFund(false); }
  };

  const handleSendNotification = async () => {
    if (!notificationTitle || !notificationMessage) return;
    setSendingNotification(true);
    try {
      let targetUserIds: string[] = [];

      if (notificationTarget === 'all') {
        targetUserIds = users.map(u => u.firebase_uid);
      } else if (notificationTarget === 'users') {
        targetUserIds = users.filter(u => u.user_type === 'user').map(u => u.firebase_uid);
      } else if (notificationTarget === 'vendors') {
        targetUserIds = vendors.map(v => v.user_id).filter(Boolean);
      } else if (notificationTarget === 'admins') {
        targetUserIds = admins.map(a => a.firebase_uid);
      } else if (notificationTarget === 'custom') {
        targetUserIds = customUserIds;
      }

      if (targetUserIds.length === 0) {
        setSuccessMessage('No recipients selected.');
        return;
      }

      await sendNotificationViaService(targetUserIds, notificationTitle, notificationMessage);
      setSuccessMessage(`Notification sent to ${targetUserIds.length} users.`);
      setShowNotificationModal(false);
      setNotificationTitle('');
      setNotificationMessage('');
      setCustomUserIds([]);
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); } finally { setSendingNotification(false); }
  };

  const handleSuspendUser = async (user: User) => {
    if (!window.confirm(`Suspend user ${user.name}?`)) return;
    try {
      await updateUserRole(user, 'suspended');
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, user_type: 'suspended', is_active: false } : u));
      await sendNotificationViaService([user.firebase_uid], 'Account Suspended', 'Your account has been suspended.');
      setSuccessMessage('User suspended.');
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); }
  };

  const handleReactivateUser = async (user: User) => {
    if (!window.confirm(`Reactivate user ${user.name}?`)) return;
    try {
      await updateUserRole(user, 'user');
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, user_type: 'user', is_active: true } : u));
      await sendNotificationViaService([user.firebase_uid], 'Account Reactivated', 'Your account has been reactivated.');
      setSuccessMessage('User reactivated.');
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); }
  };

  const handleDeleteUser = async (user: User) => {
    if (!window.confirm(`⚠️ Permanently delete user ${user.name}?`)) return;
    try {
      await supabase.from('users').delete().eq('id', user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
      setSuccessMessage('User deleted.');
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); }
  };

  const handleToggleProductActive = async (product: Product) => {
    try {
      await supabase
        .from('products')
        .update({ is_active: !product.is_active, updated_at: new Date().toISOString() })
        .eq('id', product.id);
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_active: !p.is_active } : p));
      setSuccessMessage(`Product ${product.is_active ? 'deactivated' : 'activated'}.`);
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); }
  };

  const handlePromoteProduct = async (product: Product) => {
    try {
      const promotionEndsAt = new Date(); promotionEndsAt.setDate(promotionEndsAt.getDate() + 30);
      await supabase
        .from('products')
        .update({ is_promoted: true, promotion_ends_at: promotionEndsAt.toISOString(), updated_at: new Date().toISOString() })
        .eq('id', product.id);
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_promoted: true } : p));
      setSuccessMessage('Product promoted for 30 days.');
    } catch (err: any) { setSuccessMessage(`Error: ${err.message}`); }
  };

  const addCustomUserId = () => {
    if (customUserIdInput.trim() && !customUserIds.includes(customUserIdInput.trim())) {
      setCustomUserIds([...customUserIds, customUserIdInput.trim()]);
      setCustomUserIdInput('');
    }
  };
  const removeCustomUserId = (id: string) => setCustomUserIds(customUserIds.filter(uid => uid !== id));

  // Ads helpers
  const getAdPlanIcon = (planId: string) => {
    switch (planId) {
      case 'startup': return <Rocket size={14} />;
      case 'advanced': return <Crown size={14} />;
      case 'premium': return <Gem size={14} />;
      case 'custom': return <Sparkles size={14} />;
      default: return <Megaphone size={14} />;
    }
  };

  const getAdPlanColor = (planId: string) => {
    switch (planId) {
      case 'startup': return '#3b82f6';
      case 'advanced': return '#9B4819';
      case 'premium': return '#7c3aed';
      case 'custom': return '#10b981';
      default: return '#6c757d';
    }
  };

  const getAdStatusColor = (status: string) => {
    switch (status) {
      case 'running': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'expired': return '#6b7280';
      case 'failed': return '#ef4444';
      default: return '#6c757d';
    }
  };

  const getAdStatusIcon = (status: string) => {
    switch (status) {
      case 'running': return <TrendingUp size={12} />;
      case 'pending': return <Clock size={12} />;
      case 'expired': return <Timer size={12} />;
      case 'failed': return <XCircle size={12} />;
      default: return <Megaphone size={12} />;
    }
  };

  const daysRemaining = (expiresAt: string | null) => {
    if (!expiresAt) return 0;
    const ms = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86400000));
  };

  // -------------------- Render --------------------
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading admin panel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <AlertCircle size={48} />
        <h3>Access Error</h3>
        <p>{error}</p>
        <button onClick={() => navigate('/')}>Go to Home</button>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="admin">
      <button className="admin-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        <Menu size={20} />
      </button>

      <aside ref={sidebarRef} className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2>GoStorez Admin</h2>
          <button className="admin-sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={16} />
          </button>
        </div>

        <nav className="admin-nav">
          <button className={`admin-nav-item ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => { setActiveTab('overview'); setSidebarOpen(false); }}><Home size={18} /><span>Overview</span></button>
          <button className={`admin-nav-item ${activeTab === 'users' ? 'active' : ''}`} onClick={() => { setActiveTab('users'); loadUsers(true); setSidebarOpen(false); }}><Users size={18} /><span>Users</span><span className="admin-badge">{stats.totalUsers}</span></button>
          <button className={`admin-nav-item ${activeTab === 'vendors' ? 'active' : ''}`} onClick={() => { setActiveTab('vendors'); loadVendors(true); setSidebarOpen(false); }}><Store size={18} /><span>Vendors</span><span className="admin-badge">{stats.totalVendors}</span></button>
          <button className={`admin-nav-item ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => { setActiveTab('orders'); loadOrders(true); setSidebarOpen(false); }}><ShoppingBag size={18} /><span>Orders</span><span className="admin-badge">{stats.totalOrders}</span></button>
          <button className={`admin-nav-item ${activeTab === 'products' ? 'active' : ''}`} onClick={() => { setActiveTab('products'); loadProducts(true); setSidebarOpen(false); }}><Package size={18} /><span>Products</span><span className="admin-badge">{stats.totalProducts}</span></button>
          <button className={`admin-nav-item ${activeTab === 'admins' ? 'active' : ''}`} onClick={() => { setActiveTab('admins'); loadAdminsFromFirestore(); setSidebarOpen(false); }}><Shield size={18} /><span>Admins</span><span className="admin-badge">{stats.totalAdmins}</span></button>
          <button className={`admin-nav-item ${activeTab === 'ads' ? 'active' : ''}`} onClick={() => { setActiveTab('ads'); loadAds(); setSidebarOpen(false); }}><Megaphone size={18} /><span>Ads</span><span className="admin-badge">{adsStats.total}</span></button>
          <button className={`admin-nav-item ${activeTab === 'verified' ? 'active' : ''}`} onClick={() => { setActiveTab('verified'); loadVerifiedShops(); setSidebarOpen(false); }}>
            <ShieldCheck size={18} />
            <span>Verified</span>
            {(verificationStats.in_progress + verificationStats.pending_review) > 0 && (
              <span className="admin-badge admin-badge-alert">{verificationStats.in_progress + verificationStats.pending_review}</span>
            )}
          </button>
          <button className={`admin-nav-item ${activeTab === 'learning' ? 'active' : ''}`} onClick={() => { setActiveTab('learning'); loadLearningVideos(); setSidebarOpen(false); }}>
            <GraduationCap size={18} />
            <span>Learning</span>
            <span className="admin-badge">{learningVideos.length}</span>
          </button>
          <button className={`admin-nav-item ${activeTab === 'learning_search_insight' ? 'active' : ''}`} onClick={() => { setActiveTab('learning_search_insight'); loadLearningInsights(); setSidebarOpen(false); }}>
            <BookOpen size={18} />
            <span>Learning Search Insight</span>
            <span className="admin-badge">{learningInsightsRows.length}</span>
          </button>
          <button className={`admin-nav-item ${activeTab === 'search_insights' ? 'active' : ''}`} onClick={() => { setActiveTab('search_insights'); loadSearchInsights(); setSidebarOpen(false); }}>
            <TrendingUp size={18} />
            <span>Search Insights</span>
            {searchInsightsRows.length > 0 && (
              <span className="admin-badge">{searchInsightsRows.length}</span>
            )}
          </button>
          <button className={`admin-nav-item ${activeTab === 'notifications' ? 'active' : ''}`} onClick={() => { setActiveTab('notifications'); loadNotifications(); setSidebarOpen(false); }}><Bell size={18} /><span>Notifications</span><span className="admin-badge">{notifications.length}</span></button>
          <button className={`admin-nav-item ${activeTab === 'feedbacks' ? 'active' : ''}`} onClick={() => { setActiveTab('feedbacks'); loadFeedbacks(); setSidebarOpen(false); }}><MessageCircleIcon size={18} /><span>Feedbacks</span><span className="admin-badge">{feedbacks.length}</span></button>
          <button className={`admin-nav-item ${activeTab === 'faq' ? 'active' : ''}`} onClick={() => { setActiveTab('faq'); loadFaqs(); setSidebarOpen(false); }}><HelpCircle size={18} /><span>FAQ</span><span className="admin-badge">{faqs.length}</span></button>
          <button className={`admin-nav-item ${activeTab === 'support' ? 'active' : ''}`} onClick={() => { setActiveTab('support'); loadSupportRecipients(); setSidebarOpen(false); }}><Headphones size={18} /><span>Support</span><span className="admin-badge">{supportRecipients.length}</span></button>
          <button className={`admin-nav-item ${activeTab === 'openmarket' ? 'active' : ''}`} onClick={() => { setActiveTab('openmarket'); loadOpenMarketData(); setSidebarOpen(false); }}><Video size={18} /><span>Community</span><span className="admin-badge">{openMarketPosts.length}</span></button>
          <button className={`admin-nav-item ${activeTab === 'funding' ? 'active' : ''}`} onClick={() => { setActiveTab('funding'); loadMoneyRecords(); setSidebarOpen(false); }}><Wallet size={18} /><span>Funding</span><span className="admin-badge">{moneyRecords.length}</span></button>
          <button className={`admin-nav-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}><Settings size={18} /><span>Settings</span></button>
        </nav>

        <div className="admin-quick-actions">
          <button className="admin-quick-action-btn" onClick={() => navigate('/payment')}>
            <ArrowDownToLine size={16} /> Deposit
          </button>
          <button className="admin-quick-action-btn" onClick={() => navigate('/admin/withdraw')}>
            <ArrowUpFromLine size={16} /> Withdraw
          </button>
        </div>

        <div className="admin-sidebar-footer">
          <button className="admin-logout-btn" onClick={() => { clearAdminSession(); auth.signOut(); }}><LogOut size={16} /><span>Logout</span></button>
        </div>
      </aside>

      <main className={`admin-main ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {successMessage && (
          <div className="admin-success-banner">
            <CheckCircle size={16} />
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage('')}><X size={14} /></button>
          </div>
        )}

        <header className="admin-header">
          <div className="admin-header-actions" style={{ marginLeft: 'auto' }}>
            {activeTab !== 'overview' && activeTab !== 'notifications' && activeTab !== 'settings' && activeTab !== 'feedbacks' && activeTab !== 'faq' && activeTab !== 'support' && activeTab !== 'openmarket' && activeTab !== 'funding' && activeTab !== 'ads' && activeTab !== 'verified' && activeTab !== 'learning' && activeTab !== 'learning_search_insight' && activeTab !== 'search_insights' && (
              <div className="admin-search-box">
                <Search size={16} />
                <input type="text" placeholder={`Search ${activeTab}...`} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                {searchTerm && <button className="admin-clear-search" onClick={() => setSearchTerm('')}><X size={14} /></button>}
              </div>
            )}

            {(activeTab === 'orders' || activeTab === 'products') && (
              <select className="admin-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All</option>
                {activeTab === 'orders' && (
                  <>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </>
                )}
                {activeTab === 'products' && (
                  <>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="promoted">Promoted</option>
                  </>
                )}
              </select>
            )}

            {activeTab === 'funding' && (
              <div className="admin-feedback-filters">
                <select
                  className="admin-filter-select"
                  value={fundingFilter}
                  onChange={(e) => setFundingFilter(e.target.value as any)}
                >
                  <option value="all">All Transactions</option>
                  <option value="deposit">Deposits</option>
                  <option value="withdrawal">Withdrawals</option>
                  <option value="transfer">Transfers</option>
                </select>

                <button
                  className={`admin-sort-btn ${fundingSort === 'desc' ? 'active' : ''}`}
                  onClick={() => setFundingSort(fundingSort === 'desc' ? 'asc' : 'desc')}
                >
                  {fundingSort === 'desc' ? <SortDesc size={14} /> : <SortAsc size={14} />}
                  {fundingSort === 'desc' ? 'Newest First' : 'Oldest First'}
                </button>

                <button className="admin-refresh-btn" onClick={() => loadMoneyRecords()} title="Refresh">
                  <RefreshCw size={14} />
                </button>
              </div>
            )}

            {activeTab === 'ads' && (
              <div className="admin-feedback-filters">
                <div className="admin-search-box" style={{ minWidth: 200 }}>
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search ads by user, target, plan..."
                    value={adsSearch}
                    onChange={(e) => setAdsSearch(e.target.value)}
                  />
                  {adsSearch && <button className="admin-clear-search" onClick={() => setAdsSearch('')}><X size={14} /></button>}
                </div>

                <select
                  className="admin-filter-select"
                  value={adsStatusFilter}
                  onChange={(e) => setAdsStatusFilter(e.target.value as any)}
                >
                  <option value="all">All Status</option>
                  <option value="running">Running</option>
                  <option value="expired">Expired</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>

                <select
                  className="admin-filter-select"
                  value={adsPlanFilter}
                  onChange={(e) => setAdsPlanFilter(e.target.value as any)}
                >
                  <option value="all">All Plans</option>
                  <option value="startup">Startup</option>
                  <option value="advanced">Advanced</option>
                  <option value="premium">Premium</option>
                  <option value="custom">Custom</option>
                </select>

                <button className="admin-refresh-btn" onClick={loadAds} disabled={adsLoading} title="Refresh">
                  <RefreshCw size={14} className={adsLoading ? 'admin-spin-icon' : ''} />
                </button>
              </div>
            )}

            {activeTab === 'verified' && (
              <div className="admin-feedback-filters">
                <div className="admin-search-box" style={{ minWidth: 200 }}>
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search by user, shop, email..."
                    value={verifiedSearch}
                    onChange={(e) => setVerifiedSearch(e.target.value)}
                  />
                  {verifiedSearch && <button className="admin-clear-search" onClick={() => setVerifiedSearch('')}><X size={14} /></button>}
                </div>

                <select
                  className="admin-filter-select"
                  value={verifiedStatusFilter}
                  onChange={(e) => setVerifiedStatusFilter(e.target.value as any)}
                >
                  <option value="all">All Status</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="in_progress">In Progress</option>
                  <option value="success">Success</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </select>

                <select
                  className="admin-filter-select"
                  value={verifiedPlanFilter}
                  onChange={(e) => setVerifiedPlanFilter(e.target.value as any)}
                >
                  <option value="all">All Plans</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                  <option value="diamond">Diamond</option>
                </select>

                <button className="admin-refresh-btn" onClick={loadVerifiedShops} disabled={verifiedLoading} title="Refresh">
                  <RefreshCw size={14} className={verifiedLoading ? 'admin-spin-icon' : ''} />
                </button>
              </div>
            )}

            {activeTab === 'feedbacks' && (
              <div className="admin-feedback-filters">
                <select
                  className="admin-filter-select"
                  value={feedbackTypeFilter}
                  onChange={(e) => setFeedbackTypeFilter(e.target.value as FeedbackFilterType)}
                >
                  <option value="all">All Types</option>
                  <option value="complaint">Complaints</option>
                  <option value="bug">Bug Reports</option>
                  <option value="suggestion">Suggestions</option>
                  <option value="feature">Feature Requests</option>
                  <option value="other">Other</option>
                </select>

                <select
                  className="admin-filter-select"
                  value={feedbackReplyFilter}
                  onChange={(e) => setFeedbackReplyFilter(e.target.value as FeedbackReplyStatus)}
                >
                  <option value="all">All</option>
                  <option value="replied">Replied</option>
                  <option value="unreplied">Unreplied</option>
                </select>

                <button
                  className={`admin-sort-btn ${feedbackSort === 'recent' ? 'active' : ''}`}
                  onClick={() => setFeedbackSort(feedbackSort === 'recent' ? 'oldest' : 'recent')}
                >
                  {feedbackSort === 'recent' ? <SortDesc size={14} /> : <SortAsc size={14} />}
                  {feedbackSort === 'recent' ? 'Recent First' : 'Oldest First'}
                </button>
              </div>
            )}

            {activeTab === 'learning' && (
              <div className="admin-feedback-filters">
                <button className="admin-refresh-btn" onClick={loadLearningVideos} disabled={learningLoading} title="Refresh">
                  <RefreshCw size={14} className={learningLoading ? 'admin-spin-icon' : ''} />
                </button>
                <button className="admin-action-button" onClick={() => openLearningForm()}>
                  <Plus size={16} /> Post New Tutorial
                </button>
              </div>
            )}

            {activeTab === 'learning_search_insight' && (
              <div className="admin-feedback-filters">
                <span className="admin-filter-select" style={{ cursor: 'default' }}>Newest First</span>
                <button className="admin-refresh-btn" onClick={loadLearningInsights} disabled={learningInsightsLoading} title="Refresh">
                  <RefreshCw size={14} className={learningInsightsLoading ? 'admin-spin-icon' : ''} />
                </button>
              </div>
            )}

            {activeTab === 'search_insights' && (
              <div className="admin-feedback-filters">
                <button
                  className={`admin-sort-btn ${showSearchInsightsFilter ? 'active' : ''}`}
                  onClick={() => setShowSearchInsightsFilter(v => !v)}
                >
                  <FilterIcon size={14} /> Filters
                </button>
                <button className="admin-refresh-btn" onClick={loadSearchInsights} disabled={searchInsightsLoading} title="Refresh">
                  <RefreshCw size={14} className={searchInsightsLoading ? 'admin-spin-icon' : ''} />
                </button>
              </div>
            )}

            {(activeTab === 'faq' || activeTab === 'support') && (
              <button className="admin-action-button" onClick={() => {
                setEditingFaq(null);
                setFaqQuestion('');
                setFaqAnswer('');
                setShowFaqModal(activeTab === 'faq');
                setShowSupportModal(activeTab === 'support');
                if (activeTab === 'support') {
                  setSupportEmail('');
                  setSupportUserId('');
                  setEditingSupport(null);
                }
              }}>
                <Plus size={16} /> Add {activeTab === 'faq' ? 'FAQ' : 'Support Recipient'}
              </button>
            )}

            {activeTab === 'notifications' && (
              <button className="admin-action-button" onClick={() => navigate('/test-notification')}>
                <Send size={16} /> New Notification
              </button>
            )}
          </div>
        </header>

        <div className="admin-tab-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="admin-overview">
              <div className="admin-overview-header">
                <img src={logo} alt="GoStorez" className="admin-overview-logo" />
                <div className="admin-overview-name">Gostorez Administrator: <span>{currentAdminName}</span></div>
                <h2 className="admin-overview-title">Dashboard Overview</h2>
              </div>

              <div className="admin-stats-grid">
                <div className="admin-stat-card" onClick={() => { setActiveTab('users'); loadUsers(true); }}>
                  <div className="admin-stat-icon users"><Users size={24} /></div>
                  <div className="admin-stat-info"><h3>Total Users</h3><p>{stats.totalUsers.toLocaleString()}</p><small>+{stats.usersLast7Days} this week</small></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('vendors'); loadVendors(true); }}>
                  <div className="admin-stat-icon vendors"><Store size={24} /></div>
                  <div className="admin-stat-info"><h3>Vendors</h3><p>{stats.totalVendors.toLocaleString()}</p></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('admins'); loadAdminsFromFirestore(); }}>
                  <div className="admin-stat-icon admins"><Shield size={24} /></div>
                  <div className="admin-stat-info"><h3>Admins</h3><p>{stats.totalAdmins.toLocaleString()}</p></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('products'); loadProducts(true); }}>
                  <div className="admin-stat-icon products"><Package size={24} /></div>
                  <div className="admin-stat-info"><h3>Products</h3><p>{stats.totalProducts.toLocaleString()}</p></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('orders'); loadOrders(true); }}>
                  <div className="admin-stat-icon orders"><ShoppingBag size={24} /></div>
                  <div className="admin-stat-info"><h3>Orders</h3><p>{stats.totalOrders.toLocaleString()}</p><small>+{stats.ordersLast7Days} this week</small></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('ads'); loadAds(); }}>
                  <div className="admin-stat-icon revenue"><Megaphone size={24} /></div>
                  <div className="admin-stat-info"><h3>Ads</h3><p>{adsStats.total.toLocaleString()}</p><small>{adsStats.running} running</small></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('verified'); loadVerifiedShops(); }}>
                  <div className="admin-stat-icon available"><ShieldCheck size={24} /></div>
                  <div className="admin-stat-info"><h3>Verified</h3><p>{verificationStats.success.toLocaleString()}</p><small>{verificationStats.in_progress + verificationStats.pending_review} pending</small></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('learning'); loadLearningVideos(); }}>
                  <div className="admin-stat-icon products"><GraduationCap size={24} /></div>
                  <div className="admin-stat-info"><h3>Tutorials</h3><p>{learningVideos.length.toLocaleString()}</p></div>
                </div>
                <div className="admin-stat-card" onClick={() => { setActiveTab('search_insights'); loadSearchInsights(); }}>
                  <div className="admin-stat-icon orders"><TrendingUp size={24} /></div>
                  <div className="admin-stat-info"><h3>Searches</h3><p>{searchInsightsRows.length.toLocaleString()}</p></div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon revenue"><TrendingUp size={24} /></div>
                  <div className="admin-stat-info"><h3>Revenue</h3><p>{formatCurrency(stats.totalRevenue)}</p></div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon balance"><DollarSign size={24} /></div>
                  <div className="admin-stat-info"><h3>User Balances</h3><p>{formatCurrency(stats.totalUserBalance)}</p></div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon pending"><Clock size={24} /></div>
                  <div className="admin-stat-info"><h3>Vendor Pending</h3><p>{formatCurrency(stats.totalVendorPending)}</p></div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon available"><CheckCircle size={24} /></div>
                  <div className="admin-stat-info"><h3>Vendor Available</h3><p>{formatCurrency(stats.totalVendorAvailable)}</p></div>
                </div>
                <div className="admin-stat-card total-money">
                  <div className="admin-stat-icon money"><CreditCard size={24} /></div>
                  <div className="admin-stat-info"><h3>Total in App</h3><p>{formatCurrency(stats.totalUserBalance + stats.totalVendorPending + stats.totalVendorAvailable)}</p></div>
                </div>
              </div>

              <div className="admin-recent-notifications">
                <h2>Recent Notifications</h2>
                <div className="admin-notification-list">
                  {notifications.length === 0 ? <p>No notifications</p> : notifications.map(n => (
                    <div key={n.id} className="admin-notification-item">
                      <Bell size={14} />
                      <div className="admin-notification-content">
                        <strong>{n.title}</strong>
                        <p>{n.body}</p>
                        <span className="admin-notification-time">{getTimeAgo(n.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <SimpleInfiniteScroll dataLength={users.length} next={() => loadUsers()} hasMore={hasMoreUsers} loader={<div className="admin-scroll-loader">Loading more users...</div>}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')}>User {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('email')}>Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th>Phone</th>
                    <th onClick={() => handleSort('balance')}>Balance {sortConfig.key === 'balance' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th>Shops</th>
                    <th onClick={() => handleSort('user_type')}>Type {sortConfig.key === 'user_type' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="admin-user-info">
                          {user.avatar_url ? <img src={user.avatar_url} alt={user.name} className="admin-avatar" /> : <div className="admin-avatar-placeholder">{user.name?.charAt(0) || 'U'}</div>}
                          <span>{user.name || 'No name'}</span>
                        </div>
                       </td>
                       <td>{user.email}</td>
                       <td>{user.phone || '—'}</td>
                      <td className="admin-balance-cell">{formatCurrency(user.balance || 0)}</td>
                       <td>{user.shops && user.shops.length > 0 ? <span className="admin-shop-count">{user.shops.length}</span> : '—'}</td>
                       <td><span className={`admin-badge-type ${user.user_type}`}>{user.user_type}</span></td>
                       <td>
                        <div className="admin-action-buttons">
                          <button className="admin-action-btn view" onClick={() => { setUserDetail(user); setShowUserDetailModal(true); }} title="View"><Eye size={16} /></button>
                          <button className="admin-action-btn credit" onClick={() => { setSelectedUser(user); setBalanceAction('credit'); setBalanceAmount(''); setBalanceReason(''); setShowUserModal(true); }} title="Credit"><Plus size={16} /></button>
                          <button className="admin-action-btn debit" onClick={() => { setSelectedUser(user); setBalanceAction('debit'); setBalanceAmount(''); setBalanceReason(''); setShowUserModal(true); }} title="Debit"><Minus size={16} /></button>
                          <button className="admin-action-btn notify" onClick={() => { setNotificationTarget('custom'); setCustomUserIds([user.firebase_uid]); setShowNotificationModal(true); }} title="Notify"><Send size={16} /></button>
                          {user.user_type !== 'admin' && user.user_type !== 'suspended' && (
                            <button className="admin-action-btn make-admin" onClick={() => handleMakeAdmin(user)} title="Make Admin"><Shield size={16} /></button>
                          )}
                          {user.user_type !== 'suspended' ? (
                            <button className="admin-action-btn suspend" onClick={() => handleSuspendUser(user)} title="Suspend"><UserX size={16} /></button>
                          ) : (
                            <button className="admin-action-btn reactivate" onClick={() => handleReactivateUser(user)} title="Reactivate"><UserCheck size={16} /></button>
                          )}
                          <button className="admin-action-btn delete" onClick={() => setDeleteTarget({ type: 'user', id: user.id, name: user.name })} title="Delete"><Trash2 size={16} /></button>
                        </div>
                       </td>
                     </tr>
                  ))}
                </tbody>
              </table>
            </SimpleInfiniteScroll>
          )}

          {/* Vendors Tab */}
          {activeTab === 'vendors' && (
            <SimpleInfiniteScroll dataLength={vendors.length} next={() => loadVendors()} hasMore={hasMoreVendors} loader={<div className="admin-scroll-loader">Loading more vendors...</div>}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('shop_name')}>Shop {sortConfig.key === 'shop_name' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('business_email')}>Email {sortConfig.key === 'business_email' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th>Phone</th>
                    <th onClick={() => handleSort('total_products')}>Products {sortConfig.key === 'total_products' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('total_sales')}>Sales {sortConfig.key === 'total_sales' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('average_rating')}>Rating {sortConfig.key === 'average_rating' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('pending_balance')}>Pending {sortConfig.key === 'pending_balance' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('available_balance')}>Available {sortConfig.key === 'available_balance' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map(vendor => (
                    <tr key={vendor.id}>
                      <td>
                        <div className="admin-user-info">
                          {vendor.profile_image ? <img src={vendor.profile_image} alt={vendor.shop_name} className="admin-avatar" /> : <div className="admin-avatar-placeholder">{vendor.shop_name?.charAt(0) || 'S'}</div>}
                          <span>{vendor.shop_name || 'Unnamed'}</span>
                        </div>
                       </td>
                       <td>{vendor.business_email || '—'}</td>
                       <td>{vendor.contact_phone || '—'}</td>
                       <td>{vendor.total_products || 0}</td>
                       <td>{vendor.total_sales || 0}</td>
                       <td><div className="admin-rating"><Star size={12} fill="currentColor" /><span>{(vendor.average_rating || 0).toFixed(1)}</span></div></td>
                      <td className="admin-balance-cell">{formatCurrency(vendor.pending_balance || 0)}</td>
                      <td className="admin-balance-cell">{formatCurrency(vendor.available_balance || 0)}</td>
                       <td>
                        <div className="admin-action-buttons">
                          <button className="admin-action-btn view" onClick={() => { setVendorDetail(vendor); setShowVendorDetailModal(true); }} title="View"><Eye size={16} /></button>
                          <button className="admin-action-btn credit" onClick={() => { setSelectedVendor(vendor); setBalanceAction('credit'); setBalanceAmount(''); setShowUserModal(true); }} title="Credit"><Plus size={16} /></button>
                          <button className="admin-action-btn debit" onClick={() => { setSelectedVendor(vendor); setBalanceAction('debit'); setBalanceAmount(''); setShowUserModal(true); }} title="Debit"><Minus size={16} /></button>
                          <button className="admin-action-btn transfer" onClick={() => { setSelectedVendor(vendor); setFundAmount(''); setShowVendorFundModal(true); }} title="Transfer"><ArrowLeftRight size={16} /></button>
                          <button className="admin-action-btn notify" onClick={() => { setNotificationTarget('custom'); setCustomUserIds([vendor.user_id]); setShowNotificationModal(true); }} title="Notify"><Send size={16} /></button>
                        </div>
                       </td>
                     </tr>
                  ))}
                </tbody>
              </table>
            </SimpleInfiniteScroll>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <SimpleInfiniteScroll dataLength={orders.length} next={() => loadOrders()} hasMore={hasMoreOrders} loader={<div className="admin-scroll-loader">Loading more orders...</div>}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('order_number')}>Order # {sortConfig.key === 'order_number' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('user_name')}>Customer {sortConfig.key === 'user_name' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('total_amount')}>Amount {sortConfig.key === 'total_amount' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('status')}>Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('payment_status')}>Payment {sortConfig.key === 'payment_status' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('created_at')}>Date {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id}>
                      <td className="admin-order-number">{order.order_number}</td>
                       <td><div>{order.user_name}</div><div className="admin-small">{order.user_email}</div></td>
                      <td className="admin-amount">{formatCurrency(order.total_amount)}</td>
                       <td><span className={`admin-status-badge ${order.status}`}>{order.status}</span></td>
                       <td><span className={`admin-payment-badge ${order.payment_status}`}>{order.payment_status}</span></td>
                       <td>{formatDate(order.created_at)}</td>
                     </tr>
                  ))}
                </tbody>
              </table>
            </SimpleInfiniteScroll>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <SimpleInfiniteScroll dataLength={products.length} next={() => loadProducts()} hasMore={hasMoreProducts} loader={<div className="admin-scroll-loader">Loading more products...</div>}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('title')}>Product {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('vendor_name')}>Vendor {sortConfig.key === 'vendor_name' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('price')}>Price {sortConfig.key === 'price' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('inventory')}>Stock {sortConfig.key === 'inventory' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('category')}>Category {sortConfig.key === 'category' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('views_count')}>Views {sortConfig.key === 'views_count' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => (
                    <tr key={product.id}>
                      <td>
                        <div className="admin-product-info">
                          {product.images && product.images[0] ? <img src={product.images[0]} alt={product.title} className="admin-product-thumb" /> : <div className="admin-product-thumb-placeholder"><Package size={16} /></div>}
                          <span className="admin-product-title">{product.title}</span>
                        </div>
                       </td>
                       <td>{product.vendor_name || '—'}</td>
                      <td className="admin-amount">{formatCurrency(product.price)}</td>
                       <td><span className={`admin-stock-badge ${product.inventory < 5 ? 'low' : 'ok'}`}>{product.inventory}</span></td>
                       <td>{product.category || '—'}</td>
                       <td>{product.views_count || 0}</td>
                       <td>
                        <span className={`admin-status-badge ${product.is_active ? 'active' : 'inactive'}`}>{product.is_active ? 'Active' : 'Inactive'}</span>
                        {product.is_promoted && <span className="admin-promo-badge">Promo</span>}
                       </td>
                       <td>
                        <div className="admin-action-buttons">
                          <button className="admin-action-btn view" title="View"><Eye size={16} /></button>
                          <button className="admin-action-btn edit" title="Edit"><Edit size={16} /></button>
                          <button className={`admin-action-btn ${product.is_active ? 'deactivate' : 'activate'}`} onClick={() => handleToggleProductActive(product)} title={product.is_active ? 'Deactivate' : 'Activate'}>{product.is_active ? <X size={16} /> : <CheckCircle size={16} />}</button>
                          {!product.is_promoted && <button className="admin-action-btn promote" onClick={() => handlePromoteProduct(product)} title="Promote"><TrendingUp size={16} /></button>}
                        </div>
                       </td>
                     </tr>
                  ))}
                </tbody>
              </table>
            </SimpleInfiniteScroll>
          )}

          {/* Admins Tab */}
          {activeTab === 'admins' && (
            <SimpleInfiniteScroll dataLength={admins.length} next={() => loadAdminsFromFirestore()} hasMore={hasMoreAdmins} loader={<div className="admin-scroll-loader">Loading more admins...</div>}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')}>Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th onClick={() => handleSort('email')}>Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}</th>
                    <th>Phone</th>
                    <th>Last Seen</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map(admin => (
                    <tr key={admin.id}>
                      <td>
                        <div className="admin-user-info">
                          {admin.avatar_url ? <img src={admin.avatar_url} alt={admin.name} className="admin-avatar" /> : <div className="admin-avatar-placeholder">{admin.name?.charAt(0) || 'A'}</div>}
                          <span>{admin.name}</span>
                        </div>
                       </td>
                       <td>{admin.email}</td>
                       <td>{admin.phone || '—'}</td>
                       <td>{admin.last_seen ? getTimeAgo(admin.last_seen) : '—'}</td>
                       <td>
                        <div className="admin-action-buttons">
                          <button className="admin-action-btn view" onClick={() => { setUserDetail(admin); setShowUserDetailModal(true); }} title="View"><Eye size={16} /></button>
                          <button className="admin-action-btn remove-admin" onClick={() => handleRemoveAdmin(admin)} title="Remove Admin"><UserMinus size={16} /></button>
                        </div>
                       </td>
                     </tr>
                  ))}
                </tbody>
              </table>
            </SimpleInfiniteScroll>
          )}

          {/* Ads Tab */}
          {activeTab === 'ads' && (
            <div className="admin-ads-container">
              <div className="admin-ads-stats">
                <div className="admin-ads-stat-card total">
                  <Megaphone size={18} />
                  <div>
                    <span>Total Ads</span>
                    <strong>{adsStats.total}</strong>
                  </div>
                </div>
                <div className="admin-ads-stat-card running">
                  <TrendingUp size={18} />
                  <div>
                    <span>Running</span>
                    <strong>{adsStats.running}</strong>
                  </div>
                </div>
                <div className="admin-ads-stat-card expired">
                  <Timer size={18} />
                  <div>
                    <span>Expired</span>
                    <strong>{adsStats.expired}</strong>
                  </div>
                </div>
                <div className="admin-ads-stat-card failed">
                  <XCircle size={18} />
                  <div>
                    <span>Failed</span>
                    <strong>{adsStats.failed}</strong>
                  </div>
                </div>
                <div className="admin-ads-stat-card revenue">
                  <Wallet size={18} />
                  <div>
                    <span>Revenue</span>
                    <strong>{formatCurrency(adsStats.totalRevenue)}</strong>
                  </div>
                </div>
              </div>

              {adsLoading ? (
                <div className="admin-ads-empty">
                  <Loader size={28} className="admin-spin-icon" />
                  <p>Loading ads...</p>
                </div>
              ) : filteredAds.length === 0 ? (
                <div className="admin-ads-empty">
                  <Megaphone size={48} />
                  <p>No ads found</p>
                  <span>Try changing filters or search</span>
                </div>
              ) : (
                <div className="admin-ads-list">
                  {filteredAds.map(ad => {
                    const planColor = getAdPlanColor(ad.plan_id);
                    const statusColor = getAdStatusColor(ad.status);
                    const remaining = ad.status === 'running' ? daysRemaining(ad.expires_at) : 0;

                    return (
                      <div
                        key={ad.id}
                        className={`admin-ad-card status-${ad.status}`}
                        onClick={() => { setSelectedAd(ad); setShowAdDetailModal(true); }}
                      >
                        <div className="admin-ad-thumb">
                          {ad.target_image ? (
                            <img
                              src={ad.target_image}
                              alt={ad.target_name || 'Ad'}
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="admin-ad-thumb-placeholder">
                              {(ad.target_name || 'A').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="admin-ad-body">
                          <div className="admin-ad-title-row">
                            <span className="admin-ad-target-name">
                              {ad.target_name || 'Untitled'}
                            </span>
                            <span
                              className="admin-ad-status"
                              style={{ color: statusColor, background: `${statusColor}18` }}
                            >
                              {getAdStatusIcon(ad.status)}
                              {ad.status}
                            </span>
                          </div>

                          <div className="admin-ad-meta">
                            <span className="admin-ad-plan" style={{ color: planColor, background: `${planColor}15` }}>
                              {getAdPlanIcon(ad.plan_id)}
                              {ad.plan_name}
                            </span>
                            <span className="admin-ad-amount">{formatCurrency(Number(ad.amount) || 0)}</span>
                            {ad.target_type && (
                              <span className="admin-ad-type">{ad.target_type}</span>
                            )}
                          </div>

                          <div className="admin-ad-user">
                            <span className="admin-ad-user-name">{ad.user_name || 'Unknown'}</span>
                            <span className="admin-ad-user-email">{ad.user_email || ''}</span>
                          </div>

                          <div className="admin-ad-dates">
                            <span>Created: {formatDate(ad.created_at)}</span>
                            {ad.status === 'running' && ad.expires_at && (
                              <span className="admin-ad-remaining">
                                <Timer size={10} /> {remaining} day{remaining !== 1 ? 's' : ''} left
                              </span>
                            )}
                            {ad.status === 'expired' && ad.expires_at && (
                              <span>Ended: {formatDate(ad.expires_at)}</span>
                            )}
                            {ad.status === 'failed' && ad.failure_reason && (
                              <span className="admin-ad-failed-reason">
                                <AlertCircle size={10} /> {ad.failure_reason}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Verified Tab */}
          {activeTab === 'verified' && (
            <div className="admin-verified-container">
              <div className="admin-verified-stats">
                <div className="admin-verified-stat-card total">
                  <ShieldCheck size={18} />
                  <div>
                    <span>Total</span>
                    <strong>{verificationStats.total}</strong>
                  </div>
                </div>
                <div className="admin-verified-stat-card pending">
                  <Clock size={18} />
                  <div>
                    <span>Pending</span>
                    <strong>{verificationStats.in_progress + verificationStats.pending_review}</strong>
                  </div>
                </div>
                <div className="admin-verified-stat-card success">
                  <CheckCircle size={18} />
                  <div>
                    <span>Verified</span>
                    <strong>{verificationStats.success}</strong>
                  </div>
                </div>
                <div className="admin-verified-stat-card rejected">
                  <XCircle size={18} />
                  <div>
                    <span>Rejected</span>
                    <strong>{verificationStats.rejected}</strong>
                  </div>
                </div>
                <div className="admin-verified-stat-card expired">
                  <Timer size={18} />
                  <div>
                    <span>Expired</span>
                    <strong>{verificationStats.expired}</strong>
                  </div>
                </div>
                <div className="admin-verified-stat-card revenue">
                  <Wallet size={18} />
                  <div>
                    <span>Revenue</span>
                    <strong>{formatCurrency(verificationStats.revenue)}</strong>
                  </div>
                </div>
              </div>

              {verifiedLoading ? (
                <div className="admin-verified-empty">
                  <Loader size={28} className="admin-spin-icon" />
                  <p>Loading verifications...</p>
                </div>
              ) : filteredVerifiedShops.length === 0 ? (
                <div className="admin-verified-empty">
                  <ShieldCheck size={48} />
                  <p>No verification requests found</p>
                  <span>Try changing filters or search</span>
                </div>
              ) : (
                <div className="admin-verified-list">
                  {filteredVerifiedShops.map(v => {
                    const statusColor =
                      v.status === 'success' ? '#10b981'
                      : v.status === 'rejected' ? '#ef4444'
                      : v.status === 'expired' ? '#6b7280'
                      : v.status === 'pending_review' ? '#f59e0b'
                      : '#3b82f6';
                    const planColor = v.plan === 'silver' ? '#94a3b8' : v.plan === 'gold' ? '#f59e0b' : '#3b82f6';

                    return (
                      <div
                        key={v.id}
                        className={`admin-verified-card status-${v.status}`}
                        onClick={() => {
                          setSelectedVerification(v);
                          setVerificationAdminNote(v.admin_note || '');
                          setVerificationNewStatus(v.status);
                          setShowVerificationModal(true);
                        }}
                      >
                        <div className="admin-verified-card-thumb">
                          {v.shop_profile_image ? (
                            <img
                              src={v.shop_profile_image}
                              alt={v.shop_name || 'Shop'}
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="admin-verified-thumb-placeholder">
                              {(v.shop_name || 'S').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="admin-verified-card-body">
                          <div className="admin-verified-title-row">
                            <span className="admin-verified-shop-name">{v.shop_name || 'Unnamed Shop'}</span>
                            <span className="admin-verified-status" style={{ color: statusColor, background: `${statusColor}18` }}>
                              {v.status.replace('_', ' ')}
                            </span>
                          </div>

                          <div className="admin-verified-meta">
                            <span className="admin-verified-plan" style={{ color: planColor, background: `${planColor}15` }}>
                              <Shield size={10} /> {v.plan}
                            </span>
                            <span className="admin-verified-amount">{formatCurrency(Number(v.fee_paid) || 0)}</span>
                          </div>

                          <div className="admin-verified-user">
                            <span className="admin-verified-user-name">{v.user_name || 'Unknown'}</span>
                            <span className="admin-verified-user-email">{v.user_email || ''}</span>
                          </div>

                          <div className="admin-verified-dates">
                            <span>Submitted: {formatDate(v.created_at)}</span>
                            <span>Updated: {formatDate(v.updated_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Learning Tab */}
          {activeTab === 'learning' && (
            <div className="admin-learning-container">
              {learningLoading && learningVideos.length === 0 ? (
                <div className="admin-learning-empty">
                  <Loader size={28} className="admin-spin-icon" />
                  <p>Loading tutorials...</p>
                </div>
              ) : learningVideos.length === 0 ? (
                <div className="admin-learning-empty">
                  <GraduationCap size={48} />
                  <p>No tutorials posted yet</p>
                  <span>Click "Post New Tutorial" to add your first one</span>
                  <button className="admin-action-button" onClick={() => openLearningForm()}>
                    <Plus size={16} /> Post New Tutorial
                  </button>
                </div>
              ) : (
                <div className="admin-learning-list">
                  {learningVideos.map(video => (
                    <div
                      key={video.id}
                      className="admin-learning-card"
                      onClick={() => {
                        setSelectedLearningVideo(video);
                        setLearningEditMode(false);
                        setShowLearningDetailModal(true);
                      }}
                    >
                      <div className="admin-learning-thumb">
                        {video.thumbnail_url ? (
                          <img
                            src={video.thumbnail_url}
                            alt={video.title}
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="admin-learning-thumb-placeholder">
                            <Play size={22} />
                          </div>
                        )}
                      </div>
                      <div className="admin-learning-body">
                        <div className="admin-learning-title-row">
                          <span className="admin-learning-title">
                            {video.title || 'Untitled Tutorial'}
                          </span>
                          <span className={`admin-learning-status ${video.is_active ? 'active' : 'inactive'}`}>
                            {video.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="admin-learning-desc">
                          {(video.description || '').substring(0, 120)}
                          {video.description && video.description.length > 120 ? '...' : ''}
                        </div>
                        <div className="admin-learning-meta">
                          <span><Eye size={10} /> {video.views_count || 0}</span>
                          <span><Star size={10} /> {(video.average_rating || 0).toFixed(1)}</span>
                          <span><MessageSquare size={10} /> {video.total_reviews || 0}</span>
                          <span><Clock size={10} /> {formatDate(video.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Learning Search Insight Tab — Newest First only */}
          {activeTab === 'learning_search_insight' && (
            <div className="admin-insights-container">
              {learningInsightsLoading && learningInsightsRows.length === 0 ? (
                <div className="admin-insights-empty">
                  <Loader size={28} className="admin-spin-icon" />
                  <p>Loading learning searches...</p>
                </div>
              ) : learningInsightsSorted.length === 0 ? (
                <div className="admin-insights-empty">
                  <BookOpen size={48} />
                  <p>No learning searches yet</p>
                  <span>Nothing has been searched on the Learning page</span>
                </div>
              ) : (
                <div className="admin-insights-list">
                  {learningInsightsSorted.map(row => (
                    <div key={row.id} className="admin-insights-row" style={{ cursor: 'default' }}>
                      <div className="admin-insights-leading">
                        {row.search_term}
                      </div>
                      <div className="admin-insights-count">
                        {formatDate(row.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Search Insights Tab — targets search_history */}
          {activeTab === 'search_insights' && (
            <div className="admin-insights-container">
              {showSearchInsightsFilter && (
                <div className="admin-insights-filter-panel">
                  <div className="admin-insights-filter-row">
                    <label>Time Range</label>
                    <select
                      className="admin-filter-select"
                      value={searchInsightsTimeFilter}
                      onChange={(e) => setSearchInsightsTimeFilter(e.target.value as TimeFilterKey)}
                    >
                      {(Object.keys(timeFilterLabels) as TimeFilterKey[]).map(k => (
                        <option key={k} value={k}>{timeFilterLabels[k]}</option>
                      ))}
                    </select>
                  </div>

                  <div className="admin-insights-filter-row">
                    <label>
                      Grouping
                      <button
                        className={`admin-toggle-btn ${searchInsightsGroupingOn ? 'on' : 'off'}`}
                        onClick={() => setSearchInsightsGroupingOn(v => !v)}
                      >
                        {searchInsightsGroupingOn ? 'ON' : 'OFF'}
                      </button>
                    </label>
                  </div>

                  {searchInsightsGroupingOn && (
                    <div className="admin-insights-filter-row">
                      <label>Similarity Threshold: {searchInsightsSimilarity}%</label>
                      <input
                        type="range"
                        min={50}
                        max={100}
                        step={5}
                        value={searchInsightsSimilarity}
                        onChange={(e) => setSearchInsightsSimilarity(Number(e.target.value))}
                      />
                    </div>
                  )}
                </div>
              )}

              {searchInsightsLoading && searchInsightsRows.length === 0 ? (
                <div className="admin-insights-empty">
                  <Loader size={28} className="admin-spin-icon" />
                  <p>Loading search insights...</p>
                </div>
              ) : searchInsightsGroups.length === 0 ? (
                <div className="admin-insights-empty">
                  <TrendingUp size={48} />
                  <p>No searches in this period</p>
                  <span>Try a wider time range</span>
                </div>
              ) : (
                <div className="admin-insights-list">
                  {searchInsightsGroups.map((group, idx) => (
                    <div
                      key={group.leading + '_' + idx}
                      className="admin-insights-row"
                      onClick={() => {
                        setSearchInsightsDetail(group);
                        setShowSearchInsightsDetailModal(true);
                      }}
                    >
                      <div className="admin-insights-leading">
                        {group.leading}
                      </div>
                      <div className="admin-insights-count">
                        {group.totalSearchCount} search{group.totalSearchCount !== 1 ? 'es' : ''}
                        {group.members.length > 1 && (
                          <span className="admin-insights-sim">
                            {' '}({group.members.length} similar matches)
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="admin-notifications-tab">
              <div className="admin-notifications-list">
                {notifications.length === 0 ? <p>No notifications</p> : notifications.map(n => (
                  <div key={n.id} className="admin-notification-item">
                    <Bell size={14} />
                    <div className="admin-notification-content">
                      <strong>{n.title}</strong>
                      <p>{n.body}</p>
                      <span className="admin-notification-time">{getTimeAgo(n.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Feedbacks Tab */}
          {activeTab === 'feedbacks' && (
            <div className="admin-feedbacks-container">
              {feedbacks.length === 0 ? (
                <div className="admin-feedbacks-empty">
                  <MessageCircleIcon size={48} />
                  <p>No feedbacks found</p>
                </div>
              ) : (
                <div className="admin-feedbacks-list">
                  {feedbacks.map((feedback) => (
                    <div key={feedback.id} className="admin-feedback-card">
                      <div className="admin-feedback-header">
                        <div className="admin-feedback-user">
                          <div className="admin-feedback-avatar">
                            {feedback.sender_name?.charAt(0) || 'U'}
                          </div>
                          <div className="admin-feedback-userinfo">
                            <span className="admin-feedback-name">{feedback.sender_name || 'Anonymous'}</span>
                            <span className="admin-feedback-email">{feedback.sender_email}</span>
                            <span className="admin-feedback-id">ID: {feedback.sender_id?.substring(0, 12)}...</span>
                          </div>
                        </div>
                        <div className="admin-feedback-meta">
                          <div className={`admin-feedback-type ${feedback.feedback_type}`}>
                            {getTypeIcon(feedback.feedback_type)}
                            <span>{getTypeLabel(feedback.feedback_type)}</span>
                          </div>
                          <div className="admin-feedback-date">{formatDate(feedback.created_at)}</div>
                        </div>
                      </div>

                      <div className="admin-feedback-question">
                        <p>{feedback.question}</p>
                      </div>

                      {feedback.answer && (
                        <div className="admin-feedback-answer">
                          <div className="admin-feedback-answer-label">
                            <Reply size={12} />
                            <span>Admin Response:</span>
                          </div>
                          <p>{feedback.answer}</p>
                        </div>
                      )}

                      <div className="admin-feedback-actions">
                        <button
                          className={`admin-feedback-approve ${feedback.approved ? 'approved' : ''}`}
                          onClick={() => handleToggleApprove(feedback)}
                          disabled={togglingApprove === feedback.id}
                        >
                          {togglingApprove === feedback.id ? (
                            <Loader size={14} className="spin" />
                          ) : (
                            <>
                              {feedback.approved ? <CheckCircle size={14} /> : <X size={14} />}
                              {feedback.approved ? 'Approved' : 'Approve'}
                            </>
                          )}
                        </button>

                        <button
                          className="admin-feedback-reply"
                          onClick={() => { setSelectedFeedback(feedback); setReplyText(feedback.answer || ''); setShowReplyModal(true); }}
                        >
                          <Reply size={14} />
                          {feedback.answer ? 'Edit Reply' : 'Reply'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <div className="admin-faq-container">
              <div className="admin-faq-list">
                {faqs.length === 0 ? (
                  <div className="admin-faq-empty">
                    <HelpCircle size={48} />
                    <p>No FAQs yet. Click "Add FAQ" to create one.</p>
                  </div>
                ) : (
                  faqs.map((faq, index) => (
                    <div key={faq.id} className="admin-faq-item">
                      <div className="admin-faq-header">
                        <div className="admin-faq-order">{index + 1}</div>
                        <div className="admin-faq-content">
                          <div className="admin-faq-question">{faq.question}</div>
                          <div className="admin-faq-answer-preview">{faq.answer.substring(0, 100)}{faq.answer.length > 100 ? '...' : ''}</div>
                        </div>
                        <div className="admin-faq-actions">
                          <button className="admin-faq-edit" onClick={() => handleEditFaq(faq)}>
                            <Edit size={16} />
                          </button>
                          <button className="admin-faq-delete" onClick={() => setDeleteTarget({ type: 'faq', id: faq.id, name: faq.question })}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && (
            <div className="admin-support-container">
              <div className="admin-support-list">
                {supportRecipients.length === 0 ? (
                  <div className="admin-support-empty">
                    <Headphones size={48} />
                    <p>No support recipients yet. Click "Add Support Recipient" to create one.</p>
                  </div>
                ) : (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>User ID (Firebase UID)</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supportRecipients.map((recipient) => (
                        <tr key={recipient.id}>
                          <td>{recipient.email || '—'}</td>
                          <td><code className="admin-support-userid">{recipient.user_id || '—'}</code></td>
                          <td>{formatDate(recipient.created_at)}</td>
                          <td>
                            <div className="admin-action-buttons">
                              <button
                                className="admin-action-btn edit"
                                onClick={() => handleEditSupport(recipient)}
                                title="Edit"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className="admin-action-btn delete"
                                onClick={() => setDeleteTarget({ type: 'support', id: recipient.id, name: recipient.email || recipient.user_id || 'Recipient' })}
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Funding Tab */}
          {activeTab === 'funding' && (
            <div className="admin-funding-container">
              <div className="admin-funding-stats">
                <div className="admin-funding-stat-card total">
                  <Wallet size={20} />
                  <div>
                    <span>Total Balance</span>
                    <strong>{formatCurrency(fundingStats.totalBalance)}</strong>
                  </div>
                </div>
                <div className="admin-funding-stat-card pending-deposit">
                  <ArrowDownToLine size={20} />
                  <div>
                    <span>Pending Deposits</span>
                    <strong>{formatCurrency(fundingStats.totalPendingDeposit)}</strong>
                  </div>
                </div>
                <div className="admin-funding-stat-card pending-withdraw">
                  <ArrowUpFromLine size={20} />
                  <div>
                    <span>Pending Withdrawals</span>
                    <strong>{formatCurrency(fundingStats.totalPendingWithdrawal)}</strong>
                  </div>
                </div>
                <div className="admin-funding-stat-card completed-deposit">
                  <CheckCircle size={20} />
                  <div>
                    <span>Completed Deposits</span>
                    <strong>{formatCurrency(fundingStats.totalCompletedDeposit)}</strong>
                  </div>
                </div>
                <div className="admin-funding-stat-card completed-withdraw">
                  <History size={20} />
                  <div>
                    <span>Completed Withdrawals</span>
                    <strong>{formatCurrency(fundingStats.totalCompletedWithdrawal)}</strong>
                  </div>
                </div>
                <div className="admin-funding-stat-card transfers">
                  <Send size={20} />
                  <div>
                    <span>Transfers</span>
                    <strong>{formatCurrency(fundingStats.totalTransfers)}</strong>
                  </div>
                </div>
              </div>

              <div className="admin-funding-transactions">
                <h3>All Transactions</h3>
                {filteredMoneyRecords.length === 0 ? (
                  <div className="admin-empty-state">
                    <History size={48} />
                    <p>No transactions found</p>
                  </div>
                ) : (
                  <div className="admin-funding-list">
                    {filteredMoneyRecords.map((record) => {
                      const amountDisplay = record.type === 'transfer' && record.recipient_id !== record.user_id
                        ? `-₦${record.amount.toLocaleString()}`
                        : record.type === 'deposit'
                          ? `+₦${record.amount.toLocaleString()}`
                          : `-₦${record.amount.toLocaleString()}`;
                      const amountColor = getAmountColor(record);

                      let displayEmail = '';
                      let displayName = '';
                      let displayAvatar = '';
                      if (record.type === 'transfer') {
                        displayEmail = record.recipient_email || '';
                        displayName = record.recipient_name || 'Recipient';
                        displayAvatar = record.recipient_avatar || '';
                      } else {
                        displayEmail = record.sender_email || '';
                        displayName = record.sender_name || 'User';
                        displayAvatar = record.sender_avatar || '';
                      }

                      return (
                        <div
                          key={record.id}
                          className="admin-funding-item"
                          onClick={() => { setSelectedTransaction(record); setShowTransactionModal(true); }}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="admin-funding-icon">
                            {record.type === 'deposit' && <ArrowDownToLine size={16} className="deposit-icon" />}
                            {record.type === 'withdrawal' && <ArrowUpFromLine size={16} className="withdraw-icon" />}
                            {record.type === 'transfer' && <Send size={16} className="transfer-icon" />}
                          </div>
                          <div className="admin-funding-details">
                            <div className="admin-funding-desc">
                              {record.type === 'deposit' && 'Deposit'}
                              {record.type === 'withdrawal' && 'Withdrawal Request'}
                              {record.type === 'transfer' && `Transfer ${record.user_id === record.user_id ? 'to' : 'from'} ${displayName}`}
                              {record.status === 'pending' && <span className="admin-funding-badge pending">Pending</span>}
                              {record.status === 'approved' && <span className="admin-funding-badge approved">Approved</span>}
                              {record.status === 'rejected' && <span className="admin-funding-badge rejected">Rejected</span>}
                              {record.status === 'completed' && <span className="admin-funding-badge completed">Completed</span>}
                            </div>
                            <div className="admin-funding-meta">
                              <span>{formatDate(record.created_at)}</span>
                              {record.bank_name && <span>• {record.bank_name}</span>}
                            </div>
                            <div className="admin-funding-user">
                              {displayAvatar ? (
                                <img src={displayAvatar} alt="" className="admin-funding-avatar" />
                              ) : (
                                <div className="admin-funding-avatar-placeholder">{displayName?.charAt(0) || 'U'}</div>
                              )}
                              <span>{displayEmail || displayName}</span>
                            </div>
                          </div>
                          <div className="admin-funding-amount" style={{ color: amountColor }}>
                            {amountDisplay}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Open Market Tab */}
          {activeTab === 'openmarket' && (
            <div className="admin-openmarket-container">
              <div className="admin-openmarket-section">
                <h2><Settings size={18} /> Market Settings</h2>
                <div className="admin-openmarket-settings-card">
                  <div className="admin-form-group">
                    <label className="admin-switch-label">
                      <input type="checkbox" checked={omActiveStatus} onChange={(e) => setOmActiveStatus(e.target.checked)} />
                      <span className="admin-switch-slider"></span>
                      <span className="admin-switch-text">Enable Open Market</span>
                    </label>
                  </div>

                  <div className="admin-form-group">
                    <label>Schedule Type</label>
                    <select value={omScheduleType} onChange={(e) => setOmScheduleType(e.target.value as 'once' | 'regular')}>
                      <option value="once">Once (specific date range)</option>
                      <option value="regular">Regular (recurring)</option>
                    </select>
                  </div>

                  <div className="admin-form-row">
                    <div className="admin-form-group">
                      <label>Open Date & Time</label>
                      <input type="datetime-local" value={omOpenDate} onChange={(e) => setOmOpenDate(e.target.value)} />
                    </div>
                    <div className="admin-form-group">
                      <label>Close Date & Time</label>
                      <input type="datetime-local" value={omCloseDate} onChange={(e) => setOmCloseDate(e.target.value)} />
                    </div>
                  </div>

                  <div className="admin-openmarket-preview">
                    <strong>Preview:</strong>
                    <span className={omActiveStatus ? 'open' : 'closed'}>
                      {omActiveStatus ? 'Market Open' : 'Market Closed'}
                    </span>
                    {omOpenDate && omCloseDate && (
                      <span className="schedule">
                        {new Date(omOpenDate).toLocaleString()} → {new Date(omCloseDate).toLocaleString()}
                      </span>
                    )}
                  </div>

                  <button className="admin-save-btn" onClick={handleSaveOpenMarketSettings} disabled={savingOpenMarket}>
                    {savingOpenMarket ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>

              <div className="admin-openmarket-section">
                <h2><Video size={18} /> Live Video Rooms</h2>
                <div className="admin-openmarket-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Room Name</th>
                        <th>Created By</th>
                        <th>Participants</th>
                        <th>Created</th>
                        <th>Last Active</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openMarketVideoRooms.map(room => (
                        <tr key={room.id}>
                          <td><strong>{room.room_name}</strong></td>
                          <td>{room.created_by_name}</td>
                          <td>{room.participant_count}</td>
                          <td>{getTimeAgo(room.created_at)}</td>
                          <td>{getTimeAgo(room.last_activity)}</td>
                          <td><span className={`admin-status-badge ${room.is_active ? 'active' : 'inactive'}`}>{room.is_active ? 'Active' : 'Ended'}</span></td>
                          <td>
                            <div className="admin-action-buttons">
                              <button className="admin-action-btn delete" onClick={() => setDeleteTarget({ type: 'openmarket_room', id: room.id, name: room.room_name })} title="Delete Room"><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {openMarketVideoRooms.length === 0 && (
                        <tr><td colSpan={7} className="admin-empty-cell">No active video rooms</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="admin-openmarket-section">
                <h2><MessageSquare size={18} /> Community Posts</h2>
                <div className="admin-openmarket-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Post</th>
                        <th>Media</th>
                        <th>Likes</th>
                        <th>Comments</th>
                        <th>Created</th>
                        <th>Expires</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openMarketPosts.map(post => (
                        <tr key={post.id}>
                          <td>
                            <div className="admin-user-info">
                              {post.user_avatar ? <img src={post.user_avatar} className="admin-avatar" /> : <div className="admin-avatar-placeholder">{post.user_name?.charAt(0) || 'U'}</div>}
                              <span>{post.user_name}</span>
                            </div>
                          </td>
                          <td className="admin-post-preview">{post.text?.substring(0, 60)}{post.text?.length > 60 ? '...' : ''}</td>
                          <td>{post.media_urls?.length || 0} media</td>
                          <td>{post.likes_count || 0}</td>
                          <td>{post.comments_count || 0}</td>
                          <td>{getTimeAgo(post.created_at)}</td>
                          <td>{getTimeAgo(post.expires_at)}</td>
                          <td>
                            <div className="admin-action-buttons">
                              <button className="admin-action-btn delete" onClick={() => setDeleteTarget({ type: 'openmarket_post', id: post.id, name: `Post by ${post.user_name}` })} title="Delete Post"><Trash2 size={16} /></button>
                              <button className="admin-action-btn block" onClick={() => handleBlockUserFromPost(post.user_id, post.user_name || 'User')} title="Block User from Open Market"><Ban size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {openMarketPosts.length === 0 && (
                        <tr><td colSpan={8} className="admin-empty-cell">No posts yet</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="admin-openmarket-section">
                <h2><UserMinus size={18} /> Blocked Users</h2>
                <div className="admin-openmarket-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>User ID (Firebase UID)</th>
                        <th>Duration</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openMarketBlacklist.map(entry => (
                        <tr key={entry.id}>
                          <td><code>{entry.user_id}</code></td>
                          <td>{getTimeAgo(entry.created_at)}</td>
                          <td>
                            <div className="admin-action-buttons">
                              <button className="admin-action-btn reactivate" onClick={() => handleUnblockFromBlacklist(entry.id, entry.user_id)} title="Unblock"><UserCheck size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {openMarketBlacklist.length === 0 && (
                        <tr><td colSpan={3} className="admin-empty-cell">No blocked users</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="admin-settings">
              <div className="admin-settings-card" onClick={() => setShowNotificationModal(true)}>
                <Send size={24} />
                <h3>Send Notification</h3>
                <p>Broadcast message to all users or vendors</p>
              </div>
              <div className="admin-stats-section">
                <h3>System Info</h3>
                <div className="admin-stat-row"><span>Total Users:</span> <strong>{stats.totalUsers}</strong></div>
                <div className="admin-stat-row"><span>Total Vendors:</span> <strong>{stats.totalVendors}</strong></div>
                <div className="admin-stat-row"><span>Total Orders:</span> <strong>{stats.totalOrders}</strong></div>
                <div className="admin-stat-row"><span>Total Products:</span> <strong>{stats.totalProducts}</strong></div>
                <div className="admin-stat-row"><span>Total Ads:</span> <strong>{adsStats.total}</strong></div>
                <div className="admin-stat-row"><span>Ad Revenue:</span> <strong>{formatCurrency(adsStats.totalRevenue)}</strong></div>
                <div className="admin-stat-row"><span>Total Verifications:</span> <strong>{verificationStats.total}</strong></div>
                <div className="admin-stat-row"><span>Verification Revenue:</span> <strong>{formatCurrency(verificationStats.revenue)}</strong></div>
                <div className="admin-stat-row"><span>Total Tutorials:</span> <strong>{learningVideos.length}</strong></div>
                <div className="admin-stat-row"><span>Marketplace Searches:</span> <strong>{searchInsightsRows.length}</strong></div>
                <div className="admin-stat-row"><span>Learning Searches:</span> <strong>{learningInsightsRows.length}</strong></div>
                <div className="admin-stat-row"><span>Total Revenue:</span> <strong>{formatCurrency(stats.totalRevenue)}</strong></div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Modals */}

      {/* Ad Detail Modal */}
      {showAdDetailModal && selectedAd && (
        <div className="admin-modal-overlay" onClick={() => setShowAdDetailModal(false)}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Ad Details</h3>
              <button className="admin-modal-close" onClick={() => setShowAdDetailModal(false)}><X size={18} /></button>
            </div>

            <div className="transaction-details">
              <div className="detail-row"><strong>ID:</strong> <code>{selectedAd.id}</code></div>
              <div className="detail-row"><strong>Status:</strong>
                <span
                  className="status-badge"
                  style={{
                    background: `${getAdStatusColor(selectedAd.status)}18`,
                    color: getAdStatusColor(selectedAd.status)
                  }}
                >
                  {selectedAd.status}
                </span>
              </div>
              <div className="detail-row"><strong>Plan:</strong>
                <span className="type-badge" style={{ background: `${getAdPlanColor(selectedAd.plan_id)}18`, color: getAdPlanColor(selectedAd.plan_id) }}>
                  {selectedAd.plan_name}
                </span>
              </div>
              <div className="detail-row"><strong>Amount:</strong> {formatCurrency(Number(selectedAd.amount) || 0)}</div>
              <div className="detail-row"><strong>Duration:</strong> {selectedAd.duration_days} days</div>

              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />

              <div className="detail-row"><strong>User:</strong> {selectedAd.user_name || '—'}</div>
              <div className="detail-row"><strong>Email:</strong> {selectedAd.user_email || '—'}</div>
              <div className="detail-row"><strong>User ID:</strong> <code>{selectedAd.user_id}</code></div>

              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />

              <div className="detail-row"><strong>Target Type:</strong> {selectedAd.target_type}</div>
              <div className="detail-row"><strong>Target Name:</strong> {selectedAd.target_name || '—'}</div>
              <div className="detail-row"><strong>Target ID:</strong> <code>{selectedAd.target_id}</code></div>
              {selectedAd.target_category && (
                <div className="detail-row"><strong>Category:</strong> {selectedAd.target_category}</div>
              )}

              {selectedAd.target_image && (
                <div className="detail-row receipt">
                  <strong>Target Image:</strong>
                  <img src={selectedAd.target_image} alt="Target" className="receipt-image" />
                  <button
                    className="admin-media-download-mini"
                    onClick={() => downloadImage(selectedAd.target_image!, `ad-${selectedAd.id}-target.jpg`)}
                    title="Download image"
                  >
                    <Download size={12} /> Download
                  </button>
                </div>
              )}

              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />

              <div className="detail-row"><strong>Started:</strong> {selectedAd.started_at ? formatDate(selectedAd.started_at) : '—'}</div>
              <div className="detail-row"><strong>Expires:</strong> {selectedAd.expires_at ? formatDate(selectedAd.expires_at) : '—'}</div>
              <div className="detail-row"><strong>Created:</strong> {formatDate(selectedAd.created_at)}</div>
              {selectedAd.updated_at && (
                <div className="detail-row"><strong>Updated:</strong> {formatDate(selectedAd.updated_at)}</div>
              )}

              {selectedAd.failure_reason && (
                <div className="detail-row" style={{ color: '#dc2626' }}>
                  <strong>Failure Reason:</strong> {selectedAd.failure_reason}
                </div>
              )}

              {selectedAd.extra && Object.keys(selectedAd.extra).length > 0 && (
                <>
                  <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />
                  <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <strong>Extra Data:</strong>
                    <pre style={{ fontSize: 10, background: '#f8f9fa', padding: 6, borderRadius: 4, marginTop: 4, width: '100%', overflowX: 'auto' }}>
                      {JSON.stringify(selectedAd.extra, null, 2)}
                    </pre>
                  </div>
                </>
              )}
            </div>

            <div className="admin-modal-actions">
              {selectedAd.target_image && (
                <button
                  className="admin-download-btn"
                  onClick={() => downloadAllAdMedia(selectedAd)}
                  title="Download all media"
                >
                  <Download size={14} />
                  Download All Media
                </button>
              )}
              <button className="admin-cancel-btn" onClick={() => setShowAdDetailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Detail Modal */}
      {showVerificationModal && selectedVerification && (
        <div className="admin-modal-overlay" onClick={() => setShowVerificationModal(false)}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Verification Details</h3>
              <button className="admin-modal-close" onClick={() => setShowVerificationModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className={`admin-verification-banner status-${selectedVerification.status}`}>
              <div className="admin-verification-banner-icon">
                {selectedVerification.status === 'success' && <CheckCircle size={20} />}
                {selectedVerification.status === 'rejected' && <XCircle size={20} />}
                {selectedVerification.status === 'expired' && <Timer size={20} />}
                {selectedVerification.status === 'pending_review' && <Clock size={20} />}
                {selectedVerification.status === 'in_progress' && <Clock size={20} />}
              </div>
              <div className="admin-verification-banner-body">
                <strong>{selectedVerification.shop_name || 'Unnamed Shop'}</strong>
                <span>{selectedVerification.plan.toUpperCase()} plan · {formatCurrency(Number(selectedVerification.fee_paid) || 0)}</span>
              </div>
            </div>

            <div className="admin-verification-info">
              <div className="detail-row"><strong>Verification ID:</strong> <code>{selectedVerification.id}</code></div>
              <div className="detail-row"><strong>Shop ID:</strong> <code>{selectedVerification.shop_id}</code></div>
              <div className="detail-row"><strong>User ID:</strong> <code>{selectedVerification.user_id}</code></div>
              <div className="detail-row"><strong>Owner:</strong> {selectedVerification.user_name || '—'}</div>
              <div className="detail-row"><strong>Email:</strong> {selectedVerification.user_email || '—'}</div>
              <div className="detail-row"><strong>Plan:</strong> {selectedVerification.plan}</div>
              <div className="detail-row"><strong>Submitted:</strong> {formatDate(selectedVerification.created_at)}</div>
              <div className="detail-row"><strong>Last Updated:</strong> {formatDate(selectedVerification.updated_at)}</div>
              {selectedVerification.expires_at && (
                <div className="detail-row"><strong>Expires:</strong> {formatDate(selectedVerification.expires_at)}</div>
              )}
            </div>

            <div className="admin-verification-docs">
              <h4>Uploaded Documents</h4>
              <div className="admin-verification-docs-grid">
                {selectedVerification.nin_image_url && (
                  <div className="admin-verification-doc">
                    <div className="admin-verification-doc-label">NIN / ID Card</div>
                    <img src={selectedVerification.nin_image_url} alt="NIN" />
                    <button
                      className="admin-media-download-mini"
                      onClick={() => downloadImage(selectedVerification.nin_image_url!, `verification-${selectedVerification.shop_id}-nin.jpg`)}
                    >
                      <Download size={12} /> Download
                    </button>
                  </div>
                )}
                {selectedVerification.proof_of_address_url && (
                  <div className="admin-verification-doc">
                    <div className="admin-verification-doc-label">Proof of Address</div>
                    <img src={selectedVerification.proof_of_address_url} alt="Proof" />
                    <button
                      className="admin-media-download-mini"
                      onClick={() => downloadImage(selectedVerification.proof_of_address_url!, `verification-${selectedVerification.shop_id}-proof.jpg`)}
                    >
                      <Download size={12} /> Download
                    </button>
                  </div>
                )}
                {selectedVerification.cac_image_url && (
                  <div className="admin-verification-doc">
                    <div className="admin-verification-doc-label">CAC Registration</div>
                    <img src={selectedVerification.cac_image_url} alt="CAC" />
                    <button
                      className="admin-media-download-mini"
                      onClick={() => downloadImage(selectedVerification.cac_image_url!, `verification-${selectedVerification.shop_id}-cac.jpg`)}
                    >
                      <Download size={12} /> Download
                    </button>
                  </div>
                )}
                {!selectedVerification.nin_image_url && !selectedVerification.proof_of_address_url && !selectedVerification.cac_image_url && (
                  <div className="admin-verification-no-docs">
                    <AlertCircle size={20} />
                    <span>No documents uploaded</span>
                  </div>
                )}
              </div>
              {(selectedVerification.nin_image_url || selectedVerification.proof_of_address_url || selectedVerification.cac_image_url) && (
                <button
                  className="admin-download-btn"
                  onClick={() => {
                    const docs = [
                      selectedVerification.nin_image_url && { url: selectedVerification.nin_image_url, name: `verification-${selectedVerification.shop_id}-nin.jpg` },
                      selectedVerification.proof_of_address_url && { url: selectedVerification.proof_of_address_url, name: `verification-${selectedVerification.shop_id}-proof.jpg` },
                      selectedVerification.cac_image_url && { url: selectedVerification.cac_image_url, name: `verification-${selectedVerification.shop_id}-cac.jpg` }
                    ].filter(Boolean) as { url: string; name: string }[];
                    docs.forEach((d, i) => setTimeout(() => downloadImage(d.url, d.name), i * 200));
                    setSuccessMessage(`Downloading ${docs.length} file${docs.length > 1 ? 's' : ''}...`);
                  }}
                  style={{ marginTop: 10, width: '100%' }}
                >
                  <Download size={14} /> Download All Documents
                </button>
              )}
            </div>

            <div className="admin-verification-controls">
              <div className="admin-form-group">
                <label>Update Status</label>
                <select
                  value={verificationNewStatus}
                  onChange={(e) => setVerificationNewStatus(e.target.value as VerifiedStatus)}
                >
                  <option value="in_progress">In Progress</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="success">Success (Approve)</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              <div className="admin-form-group">
                <label>Admin Note (visible to user)</label>
                <textarea
                  rows={3}
                  value={verificationAdminNote}
                  onChange={(e) => setVerificationAdminNote(e.target.value)}
                  placeholder="e.g., NIN image is blurry, please re-upload."
                />
              </div>
            </div>

            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowVerificationModal(false)}>
                Cancel
              </button>
              <button
                className="admin-send-btn"
                onClick={handleSaveVerification}
                disabled={savingVerification}
              >
                {savingVerification ? (
                  <><Loader size={14} className="admin-spin-icon" /> Saving...</>
                ) : (
                  <><Check size={14} /> Save Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learning Form Modal */}
      {showLearningFormModal && (
        <div className="admin-modal-overlay" onClick={() => setShowLearningFormModal(false)}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{learningForm.id ? 'Edit Tutorial' : 'Post New Tutorial'}</h3>
              <button className="admin-modal-close" onClick={() => setShowLearningFormModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="admin-form-group">
              <label>Title (optional)</label>
              <input
                type="text"
                value={learningForm.title}
                onChange={(e) => setLearningForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Tutorial title"
              />
            </div>

            <div className="admin-form-group">
              <label>Description (optional)</label>
              <textarea
                rows={4}
                value={learningForm.description}
                onChange={(e) => setLearningForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What will viewers learn?"
              />
            </div>

            <div className="admin-form-group">
              <label>Uploaded Video URL (optional)</label>
              <input
                type="text"
                value={learningForm.video_url}
                onChange={(e) => setLearningForm(prev => ({ ...prev, video_url: e.target.value }))}
                placeholder="https://…/storage/v1/object/public/…"
              />
            </div>

            <div className="admin-form-group">
              <label>External Video Link 1 (optional)</label>
              <input
                type="text"
                value={learningForm.video_url_1}
                onChange={(e) => setLearningForm(prev => ({ ...prev, video_url_1: e.target.value }))}
                placeholder="https://youtube.com/watch?v=…"
              />
            </div>

            <div className="admin-form-group">
              <label>External Video Link 2 (optional)</label>
              <input
                type="text"
                value={learningForm.video_url_2}
                onChange={(e) => setLearningForm(prev => ({ ...prev, video_url_2: e.target.value }))}
                placeholder="https://youtu.be/…"
              />
            </div>

            <div className="admin-form-group">
              <label>External Video Link 3 (optional)</label>
              <input
                type="text"
                value={learningForm.video_url_3}
                onChange={(e) => setLearningForm(prev => ({ ...prev, video_url_3: e.target.value }))}
              />
            </div>

            <div className="admin-form-group">
              <label>External Video Link 4 (optional)</label>
              <input
                type="text"
                value={learningForm.video_url_4}
                onChange={(e) => setLearningForm(prev => ({ ...prev, video_url_4: e.target.value }))}
              />
            </div>

            <div className="admin-form-group">
              <label>External Video Link 5 (optional)</label>
              <input
                type="text"
                value={learningForm.video_url_5}
                onChange={(e) => setLearningForm(prev => ({ ...prev, video_url_5: e.target.value }))}
              />
            </div>

            <div className="admin-form-group">
              <label>Thumbnail URL (optional)</label>
              <input
                type="text"
                value={learningForm.thumbnail_url}
                onChange={(e) => setLearningForm(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                placeholder="https://…"
              />
            </div>

            <div className="admin-form-group">
              <label className="admin-switch-label">
                <input
                  type="checkbox"
                  checked={learningForm.is_active}
                  onChange={(e) => setLearningForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <span className="admin-switch-slider"></span>
                <span className="admin-switch-text">Active (visible to users)</span>
              </label>
            </div>

            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowLearningFormModal(false)}>Cancel</button>
              <button className="admin-send-btn" onClick={handleSaveLearning} disabled={savingLearning}>
                {savingLearning ? (
                  <><Loader size={14} className="admin-spin-icon" /> Saving...</>
                ) : (
                  <><Save size={14} /> {learningForm.id ? 'Save Changes' : 'Post Tutorial'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learning Detail Modal */}
      {showLearningDetailModal && selectedLearningVideo && (
        <div className="admin-modal-overlay" onClick={() => { setShowLearningDetailModal(false); setSelectedLearningVideo(null); }}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  className="admin-modal-close"
                  onClick={() => { setShowLearningDetailModal(false); setSelectedLearningVideo(null); }}
                >
                  <ArrowLeftRight size={18} />
                </button>
                <h3 style={{ margin: 0 }}>{selectedLearningVideo.title || 'Untitled Tutorial'}</h3>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="admin-action-btn edit"
                  title={learningEditMode ? 'Disable edit mode' : 'Enable edit mode'}
                  onClick={() => setLearningEditMode(v => !v)}
                >
                  <Edit size={16} />
                </button>
                <button
                  className="admin-action-btn delete"
                  title="Delete tutorial"
                  onClick={() => { setLearningDeleteTarget(selectedLearningVideo); setShowLearningDeleteModal(true); }}
                >
                  <Trash2 size={16} />
                </button>
                <button
                  className="admin-modal-close"
                  onClick={() => { setShowLearningDetailModal(false); setSelectedLearningVideo(null); }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {selectedLearningVideo.thumbnail_url && (
              <img
                src={selectedLearningVideo.thumbnail_url}
                alt="thumbnail"
                style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}

            <div className="transaction-details">
              <div className="detail-row"><strong>ID:</strong> <code>{selectedLearningVideo.id}</code></div>
              <div className="detail-row"><strong>Status:</strong>
                <span className={`admin-status-badge ${selectedLearningVideo.is_active ? 'active' : 'inactive'}`}>
                  {selectedLearningVideo.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="detail-row"><strong>Views:</strong> {selectedLearningVideo.views_count || 0}</div>
              <div className="detail-row"><strong>Reviews:</strong> {selectedLearningVideo.total_reviews || 0}</div>
              <div className="detail-row"><strong>Avg Rating:</strong> {(selectedLearningVideo.average_rating || 0).toFixed(2)}</div>
              <div className="detail-row"><strong>Total Stars:</strong> {selectedLearningVideo.total_stars || 0}</div>

              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />

              <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <strong>Description:</strong>
                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#333', lineHeight: 1.5 }}>
                  {selectedLearningVideo.description || '—'}
                </p>
              </div>

              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />

              <div className="detail-row"><strong>Uploaded Video:</strong> {selectedLearningVideo.video_url || '—'}</div>
              <div className="detail-row"><strong>Link 1:</strong> {selectedLearningVideo.video_url_1 || '—'}</div>
              <div className="detail-row"><strong>Link 2:</strong> {selectedLearningVideo.video_url_2 || '—'}</div>
              <div className="detail-row"><strong>Link 3:</strong> {selectedLearningVideo.video_url_3 || '—'}</div>
              <div className="detail-row"><strong>Link 4:</strong> {selectedLearningVideo.video_url_4 || '—'}</div>
              <div className="detail-row"><strong>Link 5:</strong> {selectedLearningVideo.video_url_5 || '—'}</div>

              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />

              <div className="detail-row"><strong>Created:</strong> {formatDate(selectedLearningVideo.created_at)}</div>
              <div className="detail-row"><strong>Updated:</strong> {formatDate(selectedLearningVideo.updated_at)}</div>

              <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid #e9ecef' }} />

              <div style={{ marginTop: 8 }}>
                <strong style={{ fontSize: 12 }}>Community Notes ({((selectedLearningVideo.notes || '').split('#').filter(Boolean)).length})</strong>

                {((selectedLearningVideo.notes || '').split('#').map(s => s.trim()).filter(Boolean)).map((note, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                    {learningEditMode ? (
                      <>
                        <input
                          type="text"
                          defaultValue={note}
                          onBlur={(e) => {
                            const val = e.target.value.trim();
                            if (val && val !== note) handleEditLearningNote(idx, val);
                          }}
                          style={{ flex: 1, padding: 6, fontSize: 12, border: '1px solid #ced4da', borderRadius: 4 }}
                        />
                        <button
                          className="admin-action-btn delete"
                          title="Delete note"
                          onClick={() => handleDeleteLearningNote(idx)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    ) : (
                      <p style={{ margin: 0, fontSize: 12, color: '#333', padding: '6px 8px', background: '#f8f9fa', borderRadius: 6, flex: 1 }}>
                        {note}
                      </p>
                    )}
                  </div>
                ))}

                {learningEditMode && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                    <input
                      type="text"
                      value={learningNoteDraft}
                      onChange={(e) => setLearningNoteDraft(e.target.value)}
                      placeholder="Add new note…"
                      style={{ flex: 1, padding: 8, fontSize: 12, border: '1px solid #ced4da', borderRadius: 6 }}
                    />
                    <button
                      className="admin-send-btn"
                      style={{ flex: '0 0 auto', padding: '8px 12px' }}
                      disabled={!learningNoteDraft.trim()}
                      onClick={handleAddLearningNote}
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => { setShowLearningDetailModal(false); setSelectedLearningVideo(null); }}>
                Close
              </button>
              <button
                className="admin-send-btn"
                onClick={() => { openLearningForm(selectedLearningVideo); setShowLearningDetailModal(false); }}
              >
                <Edit size={14} /> Edit Tutorial
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learning Delete Confirmation */}
      {showLearningDeleteModal && learningDeleteTarget && (
        <div className="admin-modal-overlay" onClick={() => setShowLearningDeleteModal(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Delete Tutorial</h3>
            <p>Permanently delete <strong>{learningDeleteTarget.title || 'this tutorial'}</strong>?</p>
            <p className="admin-warning">This action cannot be undone.</p>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowLearningDeleteModal(false)}>Cancel</button>
              <button className="admin-delete-btn" onClick={handleDeleteLearning}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}

      {/* Search Insights Detail Modal */}
      {showSearchInsightsDetailModal && searchInsightsDetail && (
        <div className="admin-modal-overlay" onClick={() => setShowSearchInsightsDetailModal(false)}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Matches for "{searchInsightsDetail.leading}"</h3>
              <button className="admin-modal-close" onClick={() => setShowSearchInsightsDetailModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
              Threshold: {searchInsightsSimilarity}% · Time range: {timeFilterLabels[searchInsightsTimeFilter]}
            </div>

            <table className="admin-table">
              <thead>
                <tr>
                  <th>Search Term</th>
                  <th>Count</th>
                  <th>Similarity</th>
                </tr>
              </thead>
              <tbody>
                {searchInsightsDetail.members.map((m, idx) => (
                  <tr key={m.term + idx}>
                    <td>{m.term}</td>
                    <td>{m.count}</td>
                    <td>{Math.round(m.similarityToLeading * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowSearchInsightsDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Details Modal (Funding Tab) */}
      {showTransactionModal && selectedTransaction && (
        <div className="admin-modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Transaction Details</h3>
              <button className="admin-modal-close" onClick={() => setShowTransactionModal(false)}><X size={18} /></button>
            </div>
            <div className="transaction-details">
              <div className="detail-row"><strong>ID:</strong> <code>{selectedTransaction.id}</code></div>
              <div className="detail-row"><strong>Type:</strong> <span className={`type-badge ${selectedTransaction.type}`}>{selectedTransaction.type}</span></div>
              <div className="detail-row"><strong>Amount:</strong> <span style={{ color: getAmountColor(selectedTransaction), fontWeight: 600 }}>{formatCurrency(selectedTransaction.amount)}</span></div>
              <div className="detail-row"><strong>Status:</strong> <span className={`status-badge ${selectedTransaction.status}`}>{selectedTransaction.status}</span></div>
              <div className="detail-row"><strong>Description:</strong> {selectedTransaction.description || '—'}</div>
              <div className="detail-row"><strong>Created At:</strong> {formatDate(selectedTransaction.created_at)}</div>

              {selectedTransaction.type === 'deposit' && selectedTransaction.receipt_base64 && (
                <div className="detail-row receipt">
                  <strong>Receipt:</strong>
                  <img src={selectedTransaction.receipt_base64} alt="Receipt" className="receipt-image" />
                </div>
              )}

              {selectedTransaction.type === 'withdrawal' && (
                <>
                  <div className="detail-row"><strong>Bank:</strong> {selectedTransaction.bank_name}</div>
                  <div className="detail-row"><strong>Account Number:</strong> {selectedTransaction.account_number}</div>
                  <div className="detail-row"><strong>Account Name:</strong> {selectedTransaction.account_name}</div>
                </>
              )}

              {selectedTransaction.type === 'transfer' && (
                <>
                  <div className="detail-row"><strong>From:</strong> {selectedTransaction.sender_email || selectedTransaction.user_id}</div>
                  <div className="detail-row"><strong>To:</strong> {selectedTransaction.recipient_email || selectedTransaction.recipient_id}</div>
                </>
              )}

              <div className="detail-row"><strong>User:</strong> {selectedTransaction.sender_name || selectedTransaction.sender_email} ({selectedTransaction.sender_email})</div>

              {selectedTransaction.admin_notes && (
                <div className="detail-row"><strong>Admin Notes:</strong> {selectedTransaction.admin_notes}</div>
              )}
            </div>

            {selectedTransaction.status === 'pending' && (
              <div className="admin-modal-actions funding-actions">
                {selectedTransaction.type === 'deposit' && (
                  <>
                    <button
                      className="admin-approve-btn"
                      onClick={() => handleApproveDeposit(selectedTransaction)}
                      disabled={processingAction}
                    >
                      {processingAction ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
                      Approve Deposit
                    </button>
                    <button
                      className="admin-decline-btn"
                      onClick={() => handleDeclineDeposit(selectedTransaction)}
                      disabled={processingAction}
                    >
                      <X size={16} />
                      Decline Deposit
                    </button>
                  </>
                )}
                {selectedTransaction.type === 'withdrawal' && (
                  <>
                    <button
                      className="admin-approve-btn"
                      onClick={() => handleApproveWithdrawal(selectedTransaction)}
                      disabled={processingAction}
                    >
                      {processingAction ? <Loader size={16} className="spin" /> : <CheckCircle size={16} />}
                      Approve & Send Transfer
                    </button>
                    <button
                      className="admin-decline-btn"
                      onClick={() => handleDeclineWithdrawal(selectedTransaction)}
                      disabled={processingAction}
                    >
                      <X size={16} />
                      Decline & Refund
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowTransactionModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Withdrawal Transfer Confirmation Modal */}
      {showWithdrawConfirm && withdrawApprovalData && (
        <div className="admin-modal-overlay" onClick={() => setShowWithdrawConfirm(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Confirm Withdrawal Transfer</h3>
            <p>You are about to process a withdrawal of <strong>{formatCurrency(withdrawApprovalData.amount)}</strong> to:</p>
            <div className="withdraw-details">
              <p><strong>Bank:</strong> {withdrawApprovalData.bankName}</p>
              <p><strong>Account Number:</strong> {withdrawApprovalData.accountNumber}</p>
              <p><strong>Account Name:</strong> {withdrawApprovalData.accountName}</p>
              <p><strong>Recipient:</strong> {withdrawApprovalData.userName} ({withdrawApprovalData.userEmail})</p>
            </div>
            <p className="admin-warning">⚠️ This action will send real money from the merchant account. Ensure details are correct!</p>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowWithdrawConfirm(false)}>Cancel</button>
              <button className="admin-confirm-btn transfer" onClick={processWithdrawalTransfer} disabled={processingAction}>
                {processingAction ? 'Processing...' : 'Confirm Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Balance Modal */}
      {showUserModal && (selectedUser || selectedVendor) && (
        <div className="admin-modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>{balanceAction === 'credit' ? 'Credit' : 'Debit'} {selectedUser ? 'User' : 'Vendor'}</h3>
            <p><strong>{selectedUser?.name || selectedVendor?.shop_name}</strong></p>
            <p>Current Balance: {formatCurrency(selectedUser?.balance || (balanceAction === 'credit' ? selectedVendor?.pending_balance : selectedVendor?.available_balance) || 0)}</p>
            <div className="admin-form-group">
              <label>Amount (₦)</label>
              <input type="number" min="0" step="100" value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value ? Number(e.target.value) : '')} placeholder="0" />
            </div>
            <div className="admin-form-group">
              <label>Reason (optional)</label>
              <input type="text" value={balanceReason} onChange={(e) => setBalanceReason(e.target.value)} placeholder="e.g., Refund, promotion" />
            </div>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowUserModal(false)}>Cancel</button>
              <button className="admin-send-btn" onClick={handleBalanceAdjust} disabled={!balanceAmount || processingBalance}>
                {processingBalance ? 'Processing...' : (balanceAction === 'credit' ? 'Credit' : 'Debit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vendor Fund Transfer Modal */}
      {showVendorFundModal && selectedVendor && (
        <div className="admin-modal-overlay" onClick={() => setShowVendorFundModal(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Transfer Funds</h3>
            <p><strong>{selectedVendor.shop_name}</strong></p>
            <p>Pending: {formatCurrency(selectedVendor.pending_balance || 0)} | Available: {formatCurrency(selectedVendor.available_balance || 0)}</p>
            <div className="admin-form-group">
              <label>Direction</label>
              <select value={fundDirection} onChange={(e) => setFundDirection(e.target.value as any)}>
                <option value="toAvailable">Pending → Available</option>
                <option value="toPending">Available → Pending</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label>Amount (₦)</label>
              <input type="number" min="0" step="100" value={fundAmount} onChange={(e) => setFundAmount(e.target.value ? Number(e.target.value) : '')} placeholder="0" />
            </div>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowVendorFundModal(false)}>Cancel</button>
              <button className="admin-send-btn" onClick={handleVendorFundTransfer} disabled={!fundAmount || processingFund}>
                {processingFund ? 'Processing...' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showNotificationModal && (
        <div className="admin-modal-overlay" onClick={() => setShowNotificationModal(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Send Notification</h3>
            <div className="admin-form-group">
              <label>Target Audience</label>
              <select value={notificationTarget} onChange={(e) => setNotificationTarget(e.target.value as any)}>
                <option value="all">All Users</option>
                <option value="users">Regular Users Only</option>
                <option value="vendors">Vendors Only</option>
                <option value="admins">Admins Only</option>
                <option value="custom">Custom User IDs</option>
              </select>
            </div>
            {notificationTarget === 'custom' && (
              <div className="admin-form-group">
                <label>User IDs (Firebase UID)</label>
                <div className="admin-multi-input">
                  <input type="text" value={customUserIdInput} onChange={(e) => setCustomUserIdInput(e.target.value)} placeholder="Enter Firebase UID" />
                  <button type="button" onClick={addCustomUserId} className="admin-add-btn">Add</button>
                </div>
                <div className="admin-tag-list">
                  {customUserIds.map(id => (
                    <span key={id} className="admin-tag">{id.substring(0, 8)}...<button onClick={() => removeCustomUserId(id)}><X size={12} /></button></span>
                  ))}
                </div>
              </div>
            )}
            <div className="admin-form-group"><label>Title</label><input type="text" value={notificationTitle} onChange={(e) => setNotificationTitle(e.target.value)} placeholder="Notification title" /></div>
            <div className="admin-form-group"><label>Message</label><textarea value={notificationMessage} onChange={(e) => setNotificationMessage(e.target.value)} rows={4} placeholder="Notification message" /></div>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowNotificationModal(false)}>Cancel</button>
              <button className="admin-send-btn" onClick={handleSendNotification} disabled={!notificationTitle || !notificationMessage || (notificationTarget === 'custom' && customUserIds.length === 0) || sendingNotification}>
                {sendingNotification ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply to Feedback Modal */}
      {showReplyModal && selectedFeedback && (
        <div className="admin-modal-overlay" onClick={() => setShowReplyModal(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>{selectedFeedback.answer ? 'Edit Reply' : 'Reply to Feedback'}</h3>
            <div className="admin-feedback-original">
              <strong>Original Feedback:</strong>
              <p>{selectedFeedback.question}</p>
              <small>From: {selectedFeedback.sender_name} ({selectedFeedback.sender_email})</small>
            </div>
            <div className="admin-form-group">
              <label>Your Reply</label>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={5}
                placeholder="Type your response here..."
              />
            </div>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowReplyModal(false)}>Cancel</button>
              <button className="admin-send-btn" onClick={handleReplyToFeedback} disabled={sendingReply || !replyText.trim()}>
                {sendingReply ? 'Sending...' : (selectedFeedback.answer ? 'Update Reply' : 'Send Reply')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit FAQ Modal */}
      {showFaqModal && (
        <div className="admin-modal-overlay" onClick={() => setShowFaqModal(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</h3>
            <div className="admin-form-group">
              <label>Question</label>
              <input
                type="text"
                value={faqQuestion}
                onChange={(e) => setFaqQuestion(e.target.value)}
                placeholder="Enter frequently asked question..."
              />
            </div>
            <div className="admin-form-group">
              <label>Answer</label>
              <textarea
                value={faqAnswer}
                onChange={(e) => setFaqAnswer(e.target.value)}
                rows={5}
                placeholder="Enter detailed answer..."
              />
            </div>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowFaqModal(false)}>Cancel</button>
              <button className="admin-send-btn" onClick={editingFaq ? handleUpdateFaq : handleAddFaq} disabled={savingFaq || !faqQuestion.trim() || !faqAnswer.trim()}>
                {savingFaq ? 'Saving...' : (editingFaq ? 'Update' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Support Modal */}
      {showSupportModal && (
        <div className="admin-modal-overlay" onClick={() => setShowSupportModal(false)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>{editingSupport ? 'Edit Support Recipient' : 'Add Support Recipient'}</h3>
            <div className="admin-form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                placeholder="support@example.com"
              />
              <small>Notifications will be sent to this email</small>
            </div>
            <div className="admin-form-group">
              <label>User ID (Firebase UID)</label>
              <input
                type="text"
                value={supportUserId}
                onChange={(e) => setSupportUserId(e.target.value)}
                placeholder="Firebase user ID"
              />
              <small>In-app and push notifications will be sent to this user</small>
            </div>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setShowSupportModal(false)}>Cancel</button>
              <button className="admin-send-btn" onClick={editingSupport ? handleUpdateSupport : handleAddSupport} disabled={savingSupport || (!supportEmail.trim() && !supportUserId.trim())}>
                {savingSupport ? 'Saving...' : (editingSupport ? 'Update' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="admin-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="admin-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to permanently delete <strong>{deleteTarget.name}</strong>?</p>
            <p className="admin-warning">This action cannot be undone.</p>
            <div className="admin-modal-actions">
              <button className="admin-cancel-btn" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="admin-delete-btn" onClick={() => {
                if (deleteTarget.type === 'user') {
                  const user = users.find(u => u.id === deleteTarget.id);
                  if (user) handleDeleteUser(user);
                } else if (deleteTarget.type === 'faq') {
                  const faq = faqs.find(f => f.id === deleteTarget.id);
                  if (faq) handleDeleteFaq(faq);
                } else if (deleteTarget.type === 'support') {
                  const recipient = supportRecipients.find(r => r.id === deleteTarget.id);
                  if (recipient) handleDeleteSupport(recipient);
                } else if (deleteTarget.type === 'openmarket_post') {
                  handleDeleteOpenMarketPost(deleteTarget.id);
                } else if (deleteTarget.type === 'openmarket_room') {
                  handleDeleteOpenMarketRoom(deleteTarget.id);
                }
                setDeleteTarget(null);
              }}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {showUserDetailModal && userDetail && (
        <div className="admin-modal-overlay" onClick={() => setShowUserDetailModal(false)}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <h3>User Details</h3>
            <div className="admin-user-detail">
              <div className="admin-detail-row"><span>Firebase UID:</span> <code>{userDetail.firebase_uid}</code></div>
              <div className="admin-detail-row"><span>Name:</span> {userDetail.name}</div>
              <div className="admin-detail-row"><span>Email:</span> {userDetail.email}</div>
              <div className="admin-detail-row"><span>Phone:</span> {userDetail.phone || '—'}</div>
              <div className="admin-detail-row"><span>Balance:</span> {formatCurrency(userDetail.balance || 0)}</div>
              <div className="admin-detail-row"><span>Total Spent:</span> {formatCurrency(userDetail.total_spent || 0)}</div>
              <div className="admin-detail-row"><span>Total Orders:</span> {userDetail.total_orders || 0}</div>
              <div className="admin-detail-row"><span>User Type:</span> <span className={`admin-badge-type ${userDetail.user_type}`}>{userDetail.user_type}</span></div>
              <div className="admin-detail-row"><span>Joined:</span> {formatDate(userDetail.created_at)}</div>
              <div className="admin-detail-row"><span>Last Seen:</span> {userDetail.last_seen ? formatDate(userDetail.last_seen) : '—'}</div>
              <div className="admin-detail-row"><span>Active:</span> {userDetail.is_active ? 'Yes' : 'No'}</div>
              {userDetail.shops && userDetail.shops.length > 0 && (
                <div className="admin-detail-row"><span>Shops:</span> <div className="admin-shop-list">{userDetail.shops.map(shop => <div key={shop.id}>• {shop.shop_name}</div>)}</div></div>
              )}
            </div>
            <div className="admin-modal-actions"><button className="admin-cancel-btn" onClick={() => setShowUserDetailModal(false)}>Close</button></div>
          </div>
        </div>
      )}

      {/* Vendor Detail Modal */}
      {showVendorDetailModal && vendorDetail && (
        <div className="admin-modal-overlay" onClick={() => setShowVendorDetailModal(false)}>
          <div className="admin-modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
            <h3>Vendor Details</h3>
            <div className="admin-user-detail">
              <div className="admin-detail-row"><span>Vendor ID:</span> <code>{vendorDetail.vendor_id}</code></div>
              <div className="admin-detail-row"><span>User ID (Firebase):</span> <code>{vendorDetail.user_id}</code></div>
              <div className="admin-detail-row"><span>Shop Name:</span> {vendorDetail.shop_name}</div>
              <div className="admin-detail-row"><span>Business Email:</span> {vendorDetail.business_email || '—'}</div>
              <div className="admin-detail-row"><span>Contact Phone:</span> {vendorDetail.contact_phone || '—'}</div>
              <div className="admin-detail-row"><span>Products:</span> {vendorDetail.total_products || 0}</div>
              <div className="admin-detail-row"><span>Sales:</span> {vendorDetail.total_sales || 0}</div>
              <div className="admin-detail-row"><span>Rating:</span> {vendorDetail.average_rating?.toFixed(1) || '0.0'}</div>
              <div className="admin-detail-row"><span>Pending Balance:</span> {formatCurrency(vendorDetail.pending_balance || 0)}</div>
              <div className="admin-detail-row"><span>Available Balance:</span> {formatCurrency(vendorDetail.available_balance || 0)}</div>
              <div className="admin-detail-row"><span>Created:</span> {formatDate(vendorDetail.created_at)}</div>
              <div className="admin-detail-row"><span>Active:</span> {vendorDetail.is_active ? 'Yes' : 'No'}</div>
            </div>
            <div className="admin-modal-actions"><button className="admin-cancel-btn" onClick={() => setShowVendorDetailModal(false)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;